// Centralized POI Database
// This file contains all Points of Interest with their complete details

// Helper function to format POI data consistently
const createPoi = (data) => {
  // Flatten contact info to top level for compatibility with MapComponent
  const contact = data.contact || {};
  delete data.contact;
  
  // Ensure categories is an array (some POIs use 'category' instead of 'categories')
  const categories = Array.isArray(data.categories) 
    ? data.categories 
    : (Array.isArray(data.category) ? data.category : []);
  delete data.category;

  return {
    // Top-level contact info for MapComponent compatibility
    phone: contact.phone || '',
    email: contact.email || '',
    website: contact.website || '',
    
    // Original data with contact info removed
    ...data,
    
    // Ensure consistent category field name
    category: categories,
    
    // Default values
    features: {
      wheelchairAccess: false,
      parking: false,
      guidedTours: false,
      audioGuide: false,
      giftShop: false,
      cafe: false,
      restrooms: false,
      ...data.features
    },
    images: data.images || [],
    tips: data.tips || [],
    bestTimeToVisit: data.bestTimeToVisit || [],
    averageVisitDuration: data.averageVisitDuration || 60,
    rating: data.rating || 0,
    reviewCount: data.reviewCount || 0,
    lastUpdated: data.lastUpdated || new Date().toISOString().split('T')[0],
    
    // Keep original contact info in a nested object as well
    contact: {
      phone: contact.phone || '',
      email: contact.email || '',
      website: contact.website || ''
    }
  };
};

export const poiDatabase = {
  // Douro Valley
  'miradouro-sao-leonardo': createPoi({
    id: 'miradouro-sao-leonardo',
    title: 'Miradouro de São Leonardo de Galafura',
    description: 'Breathtaking viewpoint offering panoramic views of the Douro Valley, considered one of the most beautiful in Portugal.',
    city: 'Douro',
    categories: ['viewpoint', 'scenic', 'nature'],
    lat: 41.1244,
    lng: -7.7086,
    contact: {
      phone: '+351 254 203 630',
      website: 'https://www.visitportugal.com'
    },
    hours: '24/7',
    price: 'Free',
    tips: [
      'Best visited at sunrise or sunset for amazing lighting',
      'Limited parking available',
      'Small cafe nearby for refreshments'
    ],
    features: {
      parking: true,
      wheelchairAccess: true,
      restrooms: false,
      cafe: true
    },
    images: [
      'https://images.unsplash.com/photo-1623078478883-9d1f9d4b8b0a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80'
    ],
    rating: 4.8,
    reviewCount: 1245,
    averageVisitDuration: 45,
    bestTimeToVisit: ['Spring', 'Autumn'],
    lastUpdated: '2025-05-15'
  }),

  'quinta-do-crasto': createPoi({
    id: 'quinta-do-crasto',
    title: 'Quinta do Crasto',
    description: 'One of the most famous wine estates in the Douro Valley, known for its high-quality ports and table wines, offering stunning views over the river and vineyards.',
    city: 'Douro',
    categories: ['winery', 'vineyard', 'wine-tasting'],
    lat: 41.1684,
    lng: -7.7285,
    contact: {
      phone: '+351 254 920 020',
      email: 'enoturismo@quintadocrasto.pt',
      website: 'https://www.quintadocrasto.pt'
    },
    hours: {
      monday: '10:00-18:00',
      tuesday: '10:00-18:00',
      wednesday: '10:00-18:00',
      thursday: '10:00-18:00',
      friday: '10:00-18:00',
      saturday: '10:00-18:00',
      sunday: '10:00-18:00',
      note: 'Last tour starts at 17:00'
    },
    price: {
      adult: 20,
      child: 10,
      senior: 15,
      student: 15,
      currency: 'EUR',
      includes: ['Tour', 'Wine tasting'],
      payment: ['cash', 'credit_card', 'debit_card']
    },
    features: {
      wheelchairAccess: true,
      parking: true,
      guidedTours: true,
      giftShop: true,
      cafe: true,
      restrooms: true,
      wineTasting: true,
      restaurant: true
    },
    rating: 4.7,
    reviewCount: 842,
    averageVisitDuration: 120,
    bestTimeToVisit: ['March to October', 'Harvest season (September-October)'],
    tips: [
      'Book the premium tasting for their best wines',
      'Don\'t miss the infinity pool with amazing views',
      'Try their famous LBV Port',
      'Best time to visit is during harvest season (September-October)'
    ],
    images: [
      'https://images.unsplash.com/photo-1592419451002-9d9b2f8e0b1c?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.1.0',
      'https://images.unsplash.com/photo-1605100804763-247f67b3557e?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.1.0'
    ],
    lastUpdated: '2025-05-15'
  }),

  'peso-da-regua': createPoi({
    id: 'peso-da-regua',
    title: 'Peso da Régua',
    description: 'The historic heart of the Douro Valley wine region, Peso da Régua is a charming riverside town that serves as the main gateway to Portugal\'s famous port wine country. Nestled along the Douro River, this picturesque town offers stunning views of terraced vineyards, historic wine cellars, and a rich cultural heritage dating back to the 18th century. Known as the capital of the Douro region, it\'s the perfect starting point for exploring the UNESCO-listed Douro Valley.',
    city: 'Douro',
    categories: ['city', 'wine-region', 'cultural', 'riverside'],
    lat: 41.1667,
    lng: -7.7833,
    contact: {
      phone: '+351 254 323 900',
      email: 'turismo@cm-pesoregua.pt',
      website: 'https://www.cm-pesoregua.pt'
    },
    hours: {
      touristOffice: '09:30-12:30, 14:00-18:00',
      wineMuseums: '10:00-18:00 (varies by location)',
      note: 'Best time to visit is during the day when museums and attractions are open'
    },
    price: {
      general: 'Free to explore the town',
      museums: '€2-5 per person',
      wineTastings: '€10-25 per person',
      currency: 'EUR',
      note: 'Prices may vary by venue and season'
    },
    features: {
      wheelchairAccess: true,
      restrooms: true,
      parking: true,
      giftShop: true,
      cafe: true,
      wifi: true,
      touristInformation: true,
      boatTours: true
    },
    images: [
      'https://images.pexels.com/photos/13721969/pexels-photo-13721969.jpeg?auto=compress&cs=tinysrgb&w=600',
      'https://images.unsplash.com/photo-1580070142211-03f8c9f3d0c9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      'https://images.unsplash.com/photo-1623078478883-9d1f9d4b8b0a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80'
    ],
    rating: 4.6,
    reviewCount: 3820,
    averageVisitDuration: 120,
    bestTimeToVisit: ['Spring', 'Autumn', 'September-October for harvest season'],
    tips: [
      'Visit the Douro Museum to learn about the region\'s winemaking history',
      'Take a scenic boat cruise along the Douro River',
      'Sample the famous Port wine at one of the local wine cellars',
      'Visit the viewpoint at Nossa Senhora dos Remédios for panoramic views',
      'Try the local specialty, "Arroz de Pato" (duck rice), at a traditional restaurant',
      'Visit during the "Festa de São Pedro" in June for local festivities'
    ],
    topAttractions: [
      'Douro Museum',
      'Rabelo Boat Tours',
      'Casa do Douro',
      'Nossa Senhora dos Remédios Viewpoint',
      'Municipal Market'
    ],
    dining: {
      cuisine: ['Portuguese', 'Regional', 'Seafood', 'Traditional'],
      priceRange: '€€-€€€',
      specialties: [
        'Arroz de Pato (duck rice)',
        'Bacalhau à Brás (codfish dish)',
        'Local cheeses and sausages',
        'Port wine and Douro DOC wines'
      ]
    },
    transportation: {
      train: 'Regular services from Porto (2 hours)',
      car: 'A4 and A24 highways connect to Porto (1.5 hours)',
      boat: 'River cruises available to Pinhão and other Douro destinations',
      parking: 'Paid parking available throughout the town'
    },
    events: {
      annual: [
        'Douro Film Harvest (September)',
        'Festa de São Pedro (June)',
        'Wine Harvest Festival (September-October)'
      ]
    },
    lastUpdated: '2024-06-06'
  }),

  'quinta-do-crasto': createPoi({
    id: 'quinta-do-crasto',
    title: 'Quinta do Crasto',
    description: 'Perched high above the Douro River, Quinta do Crasto is one of the most prestigious wine estates in the Douro Valley, known for its exceptional Port and Douro DOC wines. With a history dating back to the 17th century, this family-owned estate combines traditional winemaking techniques with modern innovation. The property features stunning terraced vineyards, a state-of-the-art winery, and breathtaking panoramic views of the Douro River and surrounding landscape.',
    city: 'Douro',
    categories: ['winery', 'wine-tasting', 'vineyard', 'scenic-view'],
    lat: 41.1684,
    lng: -7.7285,
    contact: {
      phone: '+351 254 920 020',
      email: 'enoturismo@quintadocrasto.pt',
      website: 'https://www.quintadocrasto.pt'
    },
    hours: {
      tours: '10:00-13:00, 14:00-18:00 (Daily, last tour at 17:00)',
      tastingRoom: '10:00-18:00 (Daily)',
      restaurant: '12:30-15:30 (Lunch by reservation)',
      note: 'Advance booking recommended, especially for tours and tastings'
    },
    price: {
      basicTour: '€15-25 per person',
      premiumTasting: '€30-50 per person',
      wineLunch: '€45-75 per person',
      currency: 'EUR',
      note: 'Prices vary by experience and season. Check website for current offerings.'
    },
    features: {
      wheelchairAccess: true,
      restrooms: true,
      parking: true,
      giftShop: true,
      restaurant: true,
      wifi: true,
      guidedTours: true,
      wineTasting: true,
      hotel: false,
      pool: true
    },
    images: [
      'https://images.pexels.com/photos/2894470/pexels-photo-2894470.jpeg?auto=compress&cs=tinysrgb&w=1200',
      'https://images.unsplash.com/photo-1580070142211-03f8c9f3d0c9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      'https://images.unsplash.com/photo-1623078478883-9d1f9d4b8b0a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80'
    ],
    rating: 4.8,
    reviewCount: 2850,
    averageVisitDuration: 120,
    bestTimeToVisit: ['Spring', 'Autumn', 'September-October for harvest'],
    topAttractions: [
      'Wine Tastings',
      'Vineyard Tours',
      'Infinity Pool with River View',
      'Wine Shop',
      'On-site Restaurant'
    ],
    tips: [
      'Book the infinity pool experience for stunning views of the Douro River',
      'Try their famous LBV (Late Bottled Vintage) Port',
      'Visit during harvest season (September-October) for a unique experience',
      'The premium tasting includes older vintages not available in the basic tasting',
      'Wear comfortable shoes for walking on uneven vineyard terrain',
      'Combine with lunch at their restaurant for a complete experience'
    ],
    dining: {
      cuisine: ['Portuguese', 'Gourmet', 'Wine Pairing'],
      priceRange: '€€€',
      specialties: [
        'Tasting menus paired with estate wines',
        'Traditional Portuguese dishes with a modern twist',
        'Local cheeses and charcuterie boards'
      ]
    },
    transportation: {
      car: '1.5 hours from Porto via A4 and N222',
      train: 'Nearest station: Pinhão (15 min by taxi)',
      taxi: 'Available from Pinhão or Peso da Régua',
      parking: 'Free on-site parking available'
    },
    events: {
      annual: [
        'Harvest Experience (September-October)',
        'Wine Blending Workshops',
        'Vineyard Picnics (Spring-Summer)'
      ]
    },
    historicalSignificance: 'The name "Crasto" comes from the Latin word "castrum," meaning Roman fort, and the estate has been producing wine for over a century. The current family owners, the Roquette family, have been at the forefront of the Douro wine revolution since the 1980s, helping to establish the region as a producer of world-class table wines in addition to its famous Port.'
  }),

  'pinhão': createPoi({
    id: 'pinhão',
    title: 'Pinhão',
    description: 'Nestled in the heart of the Douro Valley, Pinhão is a picturesque riverside village that epitomizes the beauty of Portugal\'s wine country. Famous for its terraced vineyards, historic wine estates, and the stunning Douro River, Pinhão is a must-visit destination for wine enthusiasts and nature lovers alike. The village is home to the iconic Pinhão Train Station, adorned with beautiful azulejo tiles depicting scenes of winemaking, and serves as a gateway to some of the region\'s most prestigious quintas (wine estates).',
    city: 'Douro',
    categories: ['village', 'wine-region', 'riverside', 'cultural'],
    lat: 41.1918,
    lng: -7.5409,
    contact: {
      phone: '+351 254 732 076',
      email: 'posto.turismo@cm-sabrosa.pt',
      website: 'https://www.cm-sabrosa.pt'
    },
    hours: {
      touristOffice: '09:30-12:30, 14:00-17:30',
      wineEstates: '10:00-18:00 (varies by quinta)',
      bestTime: 'Visit during daylight hours to enjoy the scenic views'
    },
    price: {
      general: 'Free to explore the village',
      wineTastings: '€10-30 per person',
      boatTours: 'Starting from €15 per person',
      currency: 'EUR',
      note: 'Prices vary by activity and season'
    },
    features: {
      wheelchairAccess: true,
      restrooms: true,
      parking: true,
      giftShop: true,
      cafe: true,
      wifi: true,
      touristInformation: true,
      boatTours: true,
      wineTastings: true
    },
    images: [
      'https://images.pexels.com/photos/20095792/pexels-photo-20095792/free-photo-of-vineyard-on-hill-over-douro-river.jpeg?auto=compress&cs=tinysrgb&w=1200',
      'https://images.unsplash.com/photo-1580070142211-03f8c9f3d0c9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      'https://images.unsplash.com/photo-1623078478883-9d1f9d4b8b0a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80'
    ],
    rating: 4.8,
    reviewCount: 5200,
    averageVisitDuration: 180,
    bestTimeToVisit: ['Spring', 'Autumn', 'September-October for harvest season'],
    topAttractions: [
      'Pinhão Train Station (azulejo tiles)',
      'Douro River Cruises',
      'Quinta do Crasto',
      'Quinta das Carvalhas',
      'São Salvador do Mundo viewpoint'
    ],
    tips: [
      'Visit the train station to see the beautiful azulejo tiles',
      'Take a scenic boat cruise along the Douro River',
      'Book wine tastings in advance at the local quintas',
      'Try the local specialty, "Posta à Mirandesa" (beef steak)',
      'Visit during the grape harvest (September-October) for a unique experience',
      'Hike to the São Salvador do Mundo viewpoint for breathtaking views'
    ],
    dining: {
      cuisine: ['Portuguese', 'Regional', 'Grilled Meats', 'Seafood'],
      priceRange: '€€-€€€',
      specialties: [
        'Posta à Mirandesa (beef steak)',
        'Bacalhau com Broa (cod with cornbread)',
        'Local cheeses and smoked meats',
        'Port wine and Douro DOC wines'
      ]
    },
    transportation: {
      train: 'Regular services from Porto (2.5 hours)',
      car: 'A4 and A24 highways connect to Porto (1.5 hours)',
      boat: 'River cruises available to Peso da Régua and other Douro destinations',
      parking: 'Paid parking available throughout the village'
    },
    events: {
      annual: [
        'Grape Harvest Festival (September-October)',
        'Douro Film Harvest (September)',
        'Wine and Jazz Festival (July)'
      ]
    },
    historicalSignificance: 'Pinhão has been at the heart of Port wine production since the 18th century. The village\'s train station, built in 1880 and later decorated with azulejo tiles in 1937, tells the story of the region\'s winemaking heritage. The surrounding landscape of terraced vineyards has been shaped by centuries of viticulture and was designated a UNESCO World Heritage Site in 2001.'
  }),

  'quinta-nova': createPoi({
    id: 'quinta-nova',
    title: 'Quinta Nova de Nossa Senhora do Carmo',
    description: 'Historic wine estate with luxury accommodation, wine tastings, and stunning views over the Douro River.',
    city: 'Douro',
    categories: ['winery', 'hotel', 'restaurant'],
    lat: 41.1667,
    lng: -7.7833,
    contact: {
      phone: '+351 254 730 430',
      email: 'reservas@quintanova.pt',
      website: 'https://www.quintanova.com'
    },
    hours: {
      monday: '10:00-18:00',
      tuesday: '10:00-18:00',
      wednesday: '10:00-18:00',
      thursday: '10:00-18:00',
      friday: '10:00-18:00',
      saturday: '10:00-18:00',
      sunday: '10:00-18:00',
      note: 'Restaurant open for lunch only, reservations required'
    },
    price: {
      adult: 15,
      child: 0,
      senior: 10,
      student: 12,
      currency: 'EUR',
      includes: ['Wine tasting', 'Vineyard tour'],
      payment: ['cash', 'credit_card', 'debit_card']
    },
    features: {
      wheelchairAccess: true,
      parking: true,
      guidedTours: true,
      giftShop: true,
      cafe: true,
      restrooms: true,
      restaurant: true,
      hotel: true
    },
    rating: 4.6,
    reviewCount: 942,
    averageVisitDuration: 120,
    bestTimeToVisit: ['March to October'],
    tips: [
      'Book the wine tasting with food pairing',
      'Stay overnight for a more immersive experience',
      'The restaurant has amazing views of the Douro River'
    ],
    images: [
      'https://images.unsplash.com/photo-1623078478883-9d1f9d4b8b0a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80'
    ],
    lastUpdated: '2025-05-15'
  }),

  // Lisbon
  'belem-tower': createPoi({
    id: 'belem-tower',
    title: 'Belém Tower',
    description: 'Iconic 16th-century fortress and UNESCO World Heritage Site at the mouth of the Tagus River.',
    city: 'Lisbon',
    categories: ['landmark', 'historic'],
    lat: 38.6916,
    lng: -9.2166,
    contact: {
      phone: '+351 21 362 0034',
      website: 'https://www.torrebelem.pt',
      email: 'info@torrebelem.pt'
    },
    hours: {
      monday: '10:00-18:30',
      tuesday: '10:00-18:30',
      wednesday: '10:00-18:30',
      thursday: '10:00-18:30',
      friday: '10:00-18:30',
      saturday: '10:00-18:30',
      sunday: '10:00-18:30',
      note: 'Closed on Mondays from October to April'
    },
    price: {
      adult: 6,
      child: 3,
      senior: 5,
      student: 5,
      family: 16,
      currency: 'EUR',
      includes: ['Tower access', 'Exhibition'],
      payment: ['cash', 'credit_card', 'debit_card']
    },
    features: {
      wheelchairAccess: true,
      parking: true,
      guidedTours: true,
      audioGuide: true,
      giftShop: true,
      cafe: false,
      restrooms: true
    },
    imageUrl: 'https://images.pexels.com/photos/30879806/pexels-photo-30879806/free-photo-of-belem-tower-at-sunset-in-lisbon-portugal.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
    images: ['https://images.pexels.com/photos/30879806/pexels-photo-30879806/free-photo-of-belem-tower-at-sunset-in-lisbon-portugal.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'],
    tips: [
      'Arrive early to avoid crowds',
      'Buy combo ticket with Jerónimos Monastery for savings',
      'Great photo spot at sunset',
      'Wear comfortable shoes for climbing the narrow stairs'
    ],
    bestTimeToVisit: ['morning', 'sunset'],
    averageVisitDuration: 60,
    rating: 4.7,
    reviewCount: 24500,
    lastUpdated: '2024-05-20',
    address: 'Av. Brasília, 1400-038 Lisboa, Portugal'
  }),
  
  'jeronimos-monastery': createPoi({
    id: 'jeronimos-monastery',
    title: 'Jerónimos Monastery',
    description: 'Gothic monastery with Manueline architecture, another UNESCO World Heritage Site.',
    city: 'Lisbon',
    categories: ['landmark', 'religious', 'historic'],
    lat: 38.6967,
    lng: -9.2072,
    contact: {
      phone: '+351 21 362 0034',
      website: 'https://www.mosteirojeronimos.pt',
      email: 'info@mosteirojeronimos.pt'
    },
    hours: {
      monday: '10:00-17:30',
      tuesday: '10:00-17:30',
      wednesday: '10:00-17:30',
      thursday: '10:00-17:30',
      friday: '10:00-17:30',
      saturday: '10:00-17:30',
      sunday: '10:00-17:30',
      note: 'Closed on Mondays from October to April'
    },
    price: {
      adult: 10,
      child: 5,
      senior: 5,
      student: 5,
      family: 20,
      currency: 'EUR',
      includes: ['Monastery access', 'Cloisters', 'Church'],
      payment: ['cash', 'credit_card', 'debit_card']
    },
    features: {
      wheelchairAccess: true,
      parking: true,
      guidedTours: true,
      audioGuide: true,
      giftShop: true,
      cafe: true,
      restrooms: true
    },
    imageUrl: 'https://images.unsplash.com/photo-1611850333417-0f7fd48df07b?q=80&w=1936&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    images: [
      'https://images.unsplash.com/photo-1611850333417-0f7fd48df07b?q=80&w=1936&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      'https://images.unsplash.com/photo-1558002038-10559006dfc2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      'https://images.unsplash.com/photo-1586788680434-30d324b2d46f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1471&q=80'
    ],
    tips: [
      'The church is free to enter',
      'Try the famous Pastéis de Belém nearby',
      'Visit early or late to avoid crowds',
      'The cloisters are the highlight - don\'t miss them!'
    ],
    bestTimeToVisit: ['morning', 'afternoon'],
    averageVisitDuration: 90,
    rating: 4.8,
    reviewCount: 32100,
    lastUpdated: '2024-05-20',
    address: 'Praça do Império 1400-206 Lisboa, Portugal'
  }),
  
  'alfama-district': createPoi({
    id: 'alfama-district',
    title: 'Alfama District',
    description: 'Lisbon\'s oldest neighborhood, known for its narrow streets, Fado music, and stunning viewpoints.',
    city: 'Lisbon',
    categories: ['neighborhood', 'historic', 'cultural'],
    lat: 38.7110,
    lng: -9.1265,
    imageUrl: 'https://images.unsplash.com/photo-1625223181444-128aab274266?q=80&w=1974&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    images: [
      'https://images.unsplash.com/photo-1625223181444-128aab274266?q=80&w=1974&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      'https://images.unsplash.com/photo-1586481273547-8c4a5a8e2e9b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      'https://images.unsplash.com/photo-1523531294919-4bcd7c65e216?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'
    ],
    contact: {
      phone: '+351 21 362 0034',
      website: 'https://www.torrebelem.pt',
      email: 'info@torrebelem.pt'
    },
    hours: {
      monday: '09:00-19:00',
      tuesday: '09:00-19:00',
      wednesday: '09:00-19:00',
      thursday: '09:00-19:00',
      friday: '09:00-19:00',
      saturday: '09:00-19:00',
      sunday: '09:00-19:00',
      note: 'Reduced hours on holidays'
    },
    price: {
      entry: 0,
      currency: 'EUR',
      note: 'Free to visit'
    },
    features: {
      wheelchairAccess: false,
      parking: false,
      guidedTours: false,
      audioGuide: false,
      giftShop: false,
      cafe: false,
      restrooms: false
    },
    tips: [
      'Explore the narrow streets and alleys',
      'Listen to Fado music at a local restaurant',
      'Visit the Lisbon Cathedral and Castle',
      'Take a tram ride to the top of the hill'
    ],
    bestTimeToVisit: ['morning', 'afternoon'],
    averageVisitDuration: 120,
    rating: 4.5,
    reviewCount: 20000,
    lastUpdated: '2024-05-20',
    address: 'Lisbon, Portugal'
  }),
  
  'miradouro-da-graca': createPoi({
    id: 'miradouro-da-graca',
    title: 'Miradouro da Graça',
    description: 'One of Lisbon\'s most beautiful viewpoints, offering panoramic views of the city and São Jorge Castle.',
    city: 'Lisbon',
    categories: ['viewpoint', 'scenic', 'free'],
    lat: 38.7147,
    lng: -9.1339,
    images: [
      'https://images.pexels.com/photos/18366596/pexels-photo-18366596/free-photo-of-buildings-on-sea-coast-in-town.jpeg'
    ],
    contact: {
      phone: '',
      website: '',
      email: ''
    },
    hours: {
      monday: '09:00-19:00',
      tuesday: '09:00-19:00',
      wednesday: '09:00-19:00',
      thursday: '09:00-19:00',
      friday: '09:00-19:00',
      saturday: '09:00-19:00',
      sunday: '09:00-19:00',
      note: 'Reduced hours on holidays'
    },
    price: {
      entry: 0,
      currency: 'EUR',
      note: 'Free to visit'
    },
    features: {
      wheelchairAccess: false,
      parking: false,
      guidedTours: false,
      audioGuide: false,
      giftShop: false,
      cafe: false,
      restrooms: false
    },
    tips: [
      'Arrive early to avoid crowds',
      'Bring a camera to capture the views',
      'Visit at sunset for a romantic atmosphere',
      'Explore the nearby neighborhood of Graça'
    ],
    bestTimeToVisit: ['morning', 'sunset'],
    averageVisitDuration: 60,
    rating: 4.5,
    reviewCount: 15000,
    lastUpdated: '2024-05-20',
    address: 'Lisbon, Portugal'
  }),
  
  // More Lisbon POIs
  'oceanario-lisboa': createPoi({
    id: 'oceanario-lisboa',
    title: 'Oceanário de Lisboa',
    description: 'One of the largest indoor aquariums in Europe, featuring a large central tank with diverse marine life.',
    city: 'Lisbon',
    categories: ['aquarium', 'family', 'educational'],
    lat: 38.7636,
    lng: -9.0959,
    imageUrl: 'https://images.unsplash.com/photo-1619554696550-ae7952e77f9d?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    images: [
      'https://images.unsplash.com/photo-1619554696550-ae7952e77f9d?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      'https://images.unsplash.com/photo-1558002038-10559006dfc2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      'https://images.unsplash.com/photo-1586788680434-30d324b2d46f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1471&q=80'
    ],
    contact: {
      phone: '+351 21 891 7000',
      website: 'https://www.oceanario.pt',
      email: 'info@oceanario.pt'
    },
    hours: {
      monday: '10:00-20:00',
      tuesday: '10:00-20:00',
      wednesday: '10:00-20:00',
      thursday: '10:00-20:00',
      friday: '10:00-20:00',
      saturday: '10:00-20:00',
      sunday: '10:00-20:00',
      note: 'Last entry 1 hour before closing'
    },
    price: {
      adult: 19,
      child: 13,
      senior: 13,
      student: 13,
      family: 50,
      currency: 'EUR'
    },
    features: {
      wheelchairAccess: true,
      parking: true,
      guidedTours: true,
      audioGuide: true,
      giftShop: true,
      cafe: true,
      restrooms: true
    },
    images: [
      'https://images.unsplash.com/photo-1501785888041-af3ef285b470?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      'https://images.unsplash.com/photo-1524303262342-7e3aaafb6a1f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'
    ],
    tips: [
      'Buy tickets online to skip the line',
      'Visit during weekdays to avoid crowds',
      'Don\'t miss the otters and penguins',
      'The ocean tank is the main attraction'
    ],
    bestTimeToVisit: ['morning', 'afternoon'],
    averageVisitDuration: 120,
    rating: 4.6,
    reviewCount: 42500,
    lastUpdated: '2024-05-20',
    address: 'Esplanada Dom Carlos I s/nº, 1990-005 Lisboa, Portugal'
  }),

  // Porto POIs
  'livraria-lello': createPoi({
    id: 'livraria-lello',
    title: 'Livraria Lello',
    description: 'One of the most beautiful bookstores in the world, famous for its stunning staircase and connection to Harry Potter.',
    city: 'Porto',
    categories: ['landmark', 'cultural', 'shopping'],
    lat: 41.1475,
    lng: -8.6153,
    contact: {
      phone: '+351 22 002 0167',
      website: 'https://www.livrarialello.pt',
      email: 'info@livrarialello.pt'
    },
    hours: {
      monday: '09:30-19:00',
      tuesday: '09:30-19:00',
      wednesday: '09:30-19:00',
      thursday: '09:30-19:00',
      friday: '09:30-19:00',
      saturday: '09:00-19:00',
      sunday: '09:00-19:00',
      note: 'Reduced hours on holidays'
    },
    price: {
      entry: 5,
      currency: 'EUR',
      note: 'Ticket cost is deductible from any book purchase'
    },
    features: {
      wheelchairAccess: true,
      giftShop: true,
      restrooms: true
    },
    images: [
      'https://images.unsplash.com/photo-1589998059331-ea2e5d5d9c3a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      'https://images.unsplash.com/photo-1589998059331-ea2e5d5d9c3a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'
    ],
    tips: [
      'Arrive early to avoid long lines',
      'The entry fee is deductible from any book purchase',
      'The upper floors offer the best photo opportunities',
      'Check out the Harry Potter inspired items in the shop'
    ],
    bestTimeToVisit: ['morning', 'late_afternoon'],
    averageVisitDuration: 60,
    rating: 4.5,
    reviewCount: 38500,
    lastUpdated: '2024-05-20',
    address: 'R. das Carmelitas 144, 4050-161 Porto, Portugal'
  }),

  // Additional POIs from activitiesByCity
  'ribeira-square': createPoi({
    id: 'ribeira-square',
    title: 'Ribeira Square',
    description: 'Colorful riverside plaza in the historic center of Porto, a UNESCO World Heritage Site.',
    city: 'Porto',
    categories: ['landmark', 'historic', 'scenic'],
    lat: 41.14,
    lng: -8.6115,
    images: ['https://images.unsplash.com/photo-1585831764599-68b6c2af2876?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      website: 'https://www.visitporto.travel/en/porto/pontos-interesse/praca-da-ribeira-e-muro-dos-bacalhoeiros',
    },
    features: {
      wheelchairAccess: true,
      cafe: true,
      restrooms: true
    },
    tips: [
      'Best visited in the evening when the square comes alive',
      'Great spot for photos of the Douro River and Dom Luís I Bridge',
      'Many restaurants and cafes with outdoor seating'
    ],
    bestTimeToVisit: ['evening', 'sunset'],
    averageVisitDuration: 60,
    rating: 4.7,
    reviewCount: 18500,
    address: 'Praça da Ribeira, 4000-510 Porto, Portugal'
  }),

  'cais-de-gaia': createPoi({
    id: 'cais-de-gaia',
    title: 'Cais de Gaia',
    description: 'Riverside area known for its port wine cellars and stunning views of Porto.',
    city: 'Porto',
    categories: ['food', 'drinks', 'scenic'],
    lat: 41.1381,
    lng: -8.6129,
    images: ['https://images.unsplash.com/photo-1571008651748-8a9a1f2a466f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      website: 'https://www.visitporto.travel/en/porto/pontos-interesse/cais-de-gaia',
    },
    features: {
      wheelchairAccess: true,
      giftShop: true,
      cafe: true,
      restrooms: true
    },
    tips: [
      'Take a port wine tasting tour',
      'Visit the Graham\'s Port Lodge for a guided tour',
      'Great spot for sunset views of Porto'
    ],
    bestTimeToVisit: ['afternoon', 'sunset'],
    averageVisitDuration: 90,
    rating: 4.6,
    reviewCount: 22400,
    address: 'Cais de Gaia, 4400-171 Vila Nova de Gaia, Portugal'
  }),

  'dom-luis-i-bridge': createPoi({
    id: 'dom-luis-i-bridge',
    title: 'Dom Luís I Bridge',
    description: 'Iconic double-deck metal arch bridge that spans the Douro River between Porto and Vila Nova de Gaia.',
    city: 'Porto',
    categories: ['landmark', 'scenic', 'historic'],
    lat: 41.1405,
    lng: -8.611,
    images: ['https://images.unsplash.com/photo-1580070142211-03f8c9f3d0c9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      website: 'https://www.visitporto.travel/en/porto/pontos-interesse/ponte-d-luis-i',
    },
    features: {
      wheelchairAccess: true
    },
    tips: [
      'Walk across the upper deck for amazing views',
      'Best light for photography is at sunset',
      'Watch for the tram crossing the bridge'
    ],
    bestTimeToVisit: ['sunset', 'evening'],
    averageVisitDuration: 30,
    rating: 4.8,
    reviewCount: 31200,
    address: 'Ponte de Dom Luís I, Porto, Portugal'
  }),

  'brasao-coliseu': createPoi({
    id: 'brasao-coliseu',
    title: 'Brasão Coliseu',
    description: 'Renowned restaurant famous for its traditional Portuguese francesinha sandwich.',
    city: 'Porto',
    categories: ['food', 'restaurant'],
    lat: 41.149,
    lng: -8.6105,
    images: ['https://images.unsplash.com/photo-1555396273-367ea4eb4db5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1374&q=80'],
    contact: {
      phone: '+351 22 201 7929',
      website: 'https://www.brasao.pt/en/restaurants/coliseu',
      email: 'coliseu@brasao.pt'
    },
    hours: {
      monday: '12:30-15:00, 19:30-23:00',
      tuesday: '12:30-15:00, 19:30-23:00',
      wednesday: '12:30-15:00, 19:30-23:00',
      thursday: '12:30-15:00, 19:30-23:00',
      friday: '12:30-15:00, 19:30-23:00',
      saturday: '12:30-23:00',
      sunday: '12:30-23:00'
    },
    price: {
      range: '€€',
      currency: 'EUR',
      note: 'Moderate prices for the quality'
    },
    features: {
      wheelchairAccess: true,
      restrooms: true
    },
    tips: [
      'Try their famous francesinha - a must when in Porto',
      'Make a reservation for dinner',
      'The steak is also highly recommended'
    ],
    bestTimeToVisit: ['lunch', 'dinner'],
    averageVisitDuration: 90,
    rating: 4.5,
    reviewCount: 12400,
    address: 'Rua de Passos Manuel 205, 4000-385 Porto, Portugal'
  }),

  'gaia-cable-car': createPoi({
    id: 'gaia-cable-car',
    title: 'Gaia Cable Car',
    description: 'Scenic cable car ride offering panoramic views of Porto, the Douro River, and the wine cellars of Vila Nova de Gaia.',
    city: 'Porto',
    categories: ['activity', 'scenic'],
    lat: 41.1385,
    lng: -8.6125,
    images: ['https://images.unsplash.com/photo-1585831764599-68b6c2af2876?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      phone: '+351 22 786 4321',
      website: 'https://www.gaiacablecar.pt/en/',
      email: 'info@gaiact.pt'
    },
    hours: {
      monday: '10:00-18:00',
      tuesday: '10:00-18:00',
      wednesday: '10:00-18:00',
      thursday: '10:00-18:00',
      friday: '10:00-19:00',
      saturday: '10:00-19:00',
      sunday: '10:00-19:00',
      note: 'Extended hours in summer'
    },
    price: {
      adult: 9.50,
      child: 5.00,
      senior: 7.50,
      student: 7.50,
      family: 25.00,
      currency: 'EUR',
      includes: ['One-way ride'],
      note: 'Round-trip tickets available at a discount'
    },
    features: {
      wheelchairAccess: true,
      giftShop: true
    },
    tips: [
      'Best time to ride is just before sunset',
      'Combine with a visit to the port wine cellars',
      'The ride is short but the views are spectacular'
    ],
    bestTimeToVisit: ['afternoon', 'sunset'],
    averageVisitDuration: 30,
    rating: 4.4,
    reviewCount: 9800,
    address: 'Cais de Gaia, Av. de Diogo Leite 70, 4400-111 Vila Nova de Gaia, Portugal'
  }),

  'livraria-lello': createPoi({
    id: 'livraria-lello',
    title: 'Livraria Lello',
    description: 'One of the most beautiful bookstores in the world, known for its stunning neo-Gothic architecture and the inspiration for J.K. Rowling\'s Harry Potter series.',
    city: 'Porto',
    categories: ['landmark', 'culture', 'historic'],
    lat: 41.1466,
    lng: -8.6147,
    images: ['https://images.unsplash.com/photo-1589992898670-2b4f1cbfb3c4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      phone: '+351 22 002 0377',
      website: 'https://www.livrarialello.pt/en',
      email: 'info@livrarialello.pt'
    },
    hours: {
      monday: '09:30-19:00',
      tuesday: '09:30-19:00',
      wednesday: '09:30-19:00',
      thursday: '09:30-19:00',
      friday: '09:30-19:00',
      saturday: '09:00-19:00',
      sunday: '09:00-19:00',
      note: 'Last entry 30 minutes before closing'
    },
    price: {
      entry: 8.00,
      currency: 'EUR',
      includes: ['Entry to the bookstore', '5€ discount on book purchases over 15.90€'],
      note: 'Tickets can be purchased online in advance to skip the line'
    },
    features: {
      wheelchairAccess: true,
      giftShop: true
    },
    tips: [
      'Arrive early or book tickets online to avoid long queues',
      'The famous red staircase is a great photo spot',
      'The upper floor offers a great view of the store'
    ],
    bestTimeToVisit: ['morning', 'weekday'],
    averageVisitDuration: 45,
    rating: 4.5,
    reviewCount: 45600,
    address: 'R. das Carmelitas 144, 4050-161 Porto, Portugal'
  }),

  'mercado-do-bolhao': createPoi({
    id: 'mercado-do-bolhao',
    title: 'Mercado do Bolhão',
    description: 'Historic market offering fresh produce, traditional Portuguese goods, and local delicacies in a beautiful 19th-century building.',
    city: 'Porto',
    categories: ['food', 'market', 'culture'],
    lat: 41.146,
    lng: -8.61,
    images: ['https://images.unsplash.com/photo-1589992898670-2b4f1cbfb3c4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      phone: '+351 22 332 4260',
      website: 'https://www.mercadobolhao.pt/',
      email: 'info@mercadobolhao.pt'
    },
    hours: {
      monday: '08:00-20:00',
      tuesday: '08:00-20:00',
      wednesday: '08:00-20:00',
      thursday: '08:00-20:00',
      friday: '08:00-20:00',
      saturday: '08:00-20:00',
      sunday: '08:00-20:00',
      note: 'Some vendors may close on Sundays and holidays'
    },
    features: {
      wheelchairAccess: true,
      restrooms: true
    },
    tips: [
      'Try the traditional Portuguese cheeses and sausages',
      'Great place to buy edible souvenirs like port wine and olive oil',
      'The market is less crowded in the morning'
    ],
    bestTimeToVisit: ['morning', 'weekday'],
    averageVisitDuration: 60,
    rating: 4.6,
    reviewCount: 23400,
    address: 'R. Formosa 4000-214, Porto, Portugal'
  }),

  'clerigos-tower': createPoi({
    id: 'clerigos-tower',
    title: 'Clérigos Tower',
    description: 'Iconic baroque bell tower offering panoramic views of Porto from its 76-meter height, part of the Clérigos Church complex.',
    city: 'Porto',
    categories: ['landmark', 'viewpoint', 'historic'],
    lat: 41.1456,
    lng: -8.6139,
    images: ['https://images.unsplash.com/photo-1589992898670-2b4f1cbfb3c4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      phone: '+351 22 200 1729',
      website: 'https://torreclerigos.pt/en/',
      email: 'info@torreclerigos.pt'
    },
    hours: {
      monday: '09:00-19:00',
      tuesday: '09:00-19:00',
      wednesday: '09:00-19:00',
      thursday: '09:00-19:00',
      friday: '09:00-19:00',
      saturday: '09:00-19:00',
      sunday: '09:00-19:00',
      note: 'Last admission 30 minutes before closing'
    },
    price: {
      adult: 8.00,
      student: 5.00,
      senior: 5.00,
      child: 0.00,
      family: 20.00,
      currency: 'EUR',
      includes: ['Access to the tower and museum'],
      note: 'Free for children under 10'
    },
    features: {
      wheelchairAccess: false,
      giftShop: true
    },
    tips: [
      'Climb the 240 steps to the top for amazing 360° views',
      'Visit early in the day to avoid crowds',
      'The church below is also worth visiting'
    ],
    bestTimeToVisit: ['morning', 'sunset'],
    averageVisitDuration: 60,
    rating: 4.7,
    reviewCount: 34200,
    address: 'R. de São Filipe de Nery, 4050-546 Porto, Portugal'
  }),

  'cantinho-do-avillez': createPoi({
    id: 'cantinho-do-avillez',
    title: 'Cantinho do Avillez',
    description: 'Upscale restaurant by celebrity chef José Avillez, offering innovative Portuguese cuisine with a modern twist in a stylish setting.',
    city: 'Porto',
    categories: ['food', 'restaurant', 'fine-dining'],
    lat: 41.145,
    lng: -8.615,
    images: ['https://images.unsplash.com/photo-1555396273-367ea4eb4db5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1374&q=80'],
    contact: {
      phone: '+351 22 208 8013',
      website: 'https://cantinhodoavillez.pt/porto/',
      email: 'porto@cantinhodoavillez.pt'
    },
    hours: {
      monday: '12:30-15:00, 19:30-23:00',
      tuesday: '12:30-15:00, 19:30-23:00',
      wednesday: '12:30-15:00, 19:30-23:00',
      thursday: '12:30-15:00, 19:30-23:00',
      friday: '12:30-15:00, 19:30-23:00',
      saturday: '12:30-23:00',
      sunday: '12:30-23:00',
      note: 'Kitchen closes between lunch and dinner service'
    },
    price: {
      range: '€€€',
      currency: 'EUR',
      note: 'Tasting menus available',
      averageMeal: 35.00
    },
    features: {
      wheelchairAccess: true,
      restrooms: true,
      reservations: true
    },
    tips: [
      'Try the famous "Aviary" egg starter',
      'Make reservations well in advance',
      'The tasting menu offers great value'
    ],
    bestTimeToVisit: ['lunch', 'dinner'],
    averageVisitDuration: 120,
    rating: 4.6,
    reviewCount: 7800,
    address: 'Rua de Mouzinho da Silveira 166, 4050-417 Porto, Portugal'
  }),

  'rua-galerias-de-paris': createPoi({
    id: 'rua-galerias-de-paris',
    title: 'Rua Galerias de Paris',
    description: 'Vibrant street in the heart of Porto known for its nightlife, lined with bars, clubs, and restaurants in beautifully preserved buildings.',
    city: 'Porto',
    categories: ['nightlife', 'food', 'culture'],
    lat: 41.1455,
    lng: -8.609,
    images: ['https://images.unsplash.com/photo-1580070142211-03f8c9f3d0c9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      website: 'https://www.visitporto.travel/en/porto/pontos-interesse/rua-das-galerias-de-paris'
    },
    hours: {
      note: 'Various establishments have different opening hours, generally from late morning until early morning'
    },
    features: {
      wheelchairAccess: true,
      restrooms: 'Varies by establishment',
      outdoorSeating: true
    },
    tips: [
      'The street comes alive after 10 PM',
      'Try the local port wine or ginjinha (cherry liqueur)',
      'Weekends are busiest, weekdays are more relaxed'
    ],
    bestTimeToVisit: ['evening', 'night'],
    averageVisitDuration: 120,
    rating: 4.5,
    reviewCount: 15600,
    address: 'Rua Galerias de Paris, 4050-161 Porto, Portugal'
  }),

  'serralves-museum': createPoi({
    id: 'serralves-museum',
    title: 'Serralves Museum',
    description: 'Portugal\'s leading contemporary art museum set in beautiful parklands, featuring works by both Portuguese and international artists.',
    city: 'Porto',
    categories: ['museum', 'art', 'park'],
    lat: 41.1693,
    lng: -8.642,
    images: ['https://images.unsplash.com/photo-1580070142211-03f8c9f3d0c9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      phone: '+351 22 615 6500',
      website: 'https://www.serralves.pt/en/',
      email: 'info@serralves.pt'
    },
    hours: {
      monday: '10:00-18:00',
      tuesday: '10:00-18:00',
      wednesday: '10:00-18:00',
      thursday: '10:00-18:00',
      friday: '10:00-18:00',
      saturday: '10:00-19:00',
      sunday: '10:00-19:00',
      note: 'Closed on December 25 and January 1'
    },
    price: {
      adult: 20.00,
      student: 14.00,
      senior: 14.00,
      child: 0.00,
      family: 38.00,
      currency: 'EUR',
      includes: ['Museum and park access'],
      note: 'Free admission on the first Sunday of each month until 1 PM'
    },
    features: {
      wheelchairAccess: true,
      giftShop: true,
      cafe: true,
      restrooms: true,
      parking: true
    },
    tips: [
      'Allocate time to explore both the museum and the beautiful gardens',
      'Free WiFi is available throughout the museum',
      'Check the website for temporary exhibitions and events'
    ],
    bestTimeToVisit: ['weekday', 'morning'],
    averageVisitDuration: 180,
    rating: 4.6,
    reviewCount: 12400,
    address: 'R. Dom João de Castro 210, 4150-417 Porto, Portugal'
  }),

  'casa-da-musica': createPoi({
    id: 'casa-da-musica',
    title: 'Casa da Música',
    description: 'Striking contemporary concert hall designed by Rem Koolhaas, hosting a diverse program of classical, jazz, and world music performances.',
    city: 'Porto',
    categories: ['music', 'architecture', 'culture'],
    lat: 41.1616,
    lng: -8.63,
    images: ['https://images.unsplash.com/photo-1571008651748-8a9a1f2a466f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      phone: '+351 22 012 0220',
      website: 'https://www.casadamusica.com/en',
      email: 'info@casadamusica.com'
    },
    hours: {
      monday: '10:00-18:00',
      tuesday: '10:00-18:00',
      wednesday: '10:00-18:00',
      thursday: '10:00-18:00',
      friday: '10:00-18:00',
      saturday: '10:00-18:00',
      sunday: '10:00-18:00',
      note: 'Box office open until 7PM on performance days'
    },
    price: {
      varies: true,
      currency: 'EUR',
      note: 'Ticket prices vary by performance',
      tour: 10.00
    },
    features: {
      wheelchairAccess: true,
      giftShop: true,
      cafe: true,
      restrooms: true
    },
    tips: [
      'Take a guided tour to appreciate the architecture',
      'The rooftop restaurant has great city views',
      'Check the website for free lunchtime concerts'
    ],
    bestTimeToVisit: ['evening', 'performance'],
    averageVisitDuration: 120,
    rating: 4.7,
    reviewCount: 14500,
    address: 'Av. da Boavista 604, 4149-071 Porto, Portugal'
  }),

  'crystal-palace-gardens': createPoi({
    id: 'crystal-palace-gardens',
    title: 'Crystal Palace Gardens',
    description: 'Beautiful public park with stunning views over the Douro River, featuring romantic gardens, peacocks, and a planetarium.',
    city: 'Porto',
    categories: ['park', 'scenic', 'nature'],
    lat: 41.1535,
    lng: -8.6108,
    images: ['https://images.unsplash.com/photo-1580070142211-03f8c9f3d0c9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      phone: '+351 22 607 7160',
      website: 'https://www.porto.pt/visit/parks-and-gardens/crystal-palace-gardens',
      email: 'divulgacao@cm-porto.pt'
    },
    hours: {
      monday: '08:00-21:00',
      tuesday: '08:00-21:00',
      wednesday: '08:00-21:00',
      thursday: '08:00-21:00',
      friday: '08:00-21:00',
      saturday: '08:00-21:00',
      sunday: '08:00-21:00',
      note: 'Hours vary by season; closes earlier in winter'
    },
    features: {
      wheelchairAccess: true,
      restrooms: true,
      picnicArea: true
    },
    tips: [
      'Great spot for a picnic with river views',
      'Look for the resident peacocks roaming freely',
      'Visit the small but interesting Romantic Museum nearby'
    ],
    bestTimeToVisit: ['morning', 'sunset'],
    averageVisitDuration: 90,
    rating: 4.6,
    reviewCount: 9800,
    address: 'R. de Dom Manuel II, 4050-346 Porto, Portugal'
  }),

  'o-gaveto': createPoi({
    id: 'o-gaveto',
    title: 'O Gaveto',
    description: 'Renowned seafood restaurant serving traditional Portuguese dishes in a cozy, unpretentious setting, famous for its fresh fish and seafood rice dishes.',
    city: 'Porto',
    categories: ['food', 'restaurant', 'seafood'],
    lat: 41.134,
    lng: -8.67,
    images: ['https://images.unsplash.com/photo-1555396273-367ea4eb4db5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1374&q=80'],
    contact: {
      phone: '+351 22 608 0234',
      website: 'https://www.ogaveto.com',
      email: 'reservas@ogaveto.com'
    },
    hours: {
      monday: '12:00-15:00, 19:30-22:30',
      tuesday: '12:00-15:00, 19:30-22:30',
      wednesday: '12:00-15:00, 19:30-22:30',
      thursday: '12:00-15:00, 19:30-22:30',
      friday: '12:00-15:00, 19:30-22:30',
      saturday: '12:00-15:00, 19:30-22:30',
      sunday: '12:00-15:00, 19:30-22:30',
      note: 'Kitchen closes between lunch and dinner service'
    },
    price: {
      range: '€€€',
      currency: 'EUR',
      averageMeal: 40.00,
      note: 'Fresh seafood priced by weight'
    },
    features: {
      wheelchairAccess: true,
      reservations: true,
      restrooms: true
    },
    tips: [
      'Try the seafood rice or cataplana',
      'Make reservations in advance, especially on weekends',
      'Ask for the daily specials'
    ],
    bestTimeToVisit: ['lunch', 'dinner'],
    averageVisitDuration: 90,
    rating: 4.7,
    reviewCount: 6800,
    address: 'Tv. de Cedofeita 14, 4050-183 Porto, Portugal'
  }),

  'foz-do-douro': createPoi({
    id: 'foz-do-douro',
    title: 'Foz do Douro',
    description: 'Charming seaside district where the Douro River meets the Atlantic Ocean, known for its scenic promenade, beaches, and upscale dining options.',
    city: 'Porto',
    categories: ['scenic', 'beach', 'food'],
    lat: 41.1383,
    lng: -8.7031,
    images: ['https://images.unsplash.com/photo-1589992898670-2b4f1cbfb3c4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      website: 'https://www.visitporto.travel/en/porto/pontos-interesse/foz-do-douro',
    },
    hours: {
      alwaysOpen: true,
      note: 'Public space accessible 24/7; individual businesses have their own hours'
    },
    features: {
      wheelchairAccess: true,
      restrooms: 'Available at nearby cafes and restaurants',
      parking: true
    },
    tips: [
      'Walk along the promenade for stunning ocean views',
      'Visit the Felgueiras Lighthouse at sunset',
      'Try the seafood at one of the many beachfront restaurants'
    ],
    bestTimeToVisit: ['afternoon', 'sunset'],
    averageVisitDuration: 120,
    rating: 4.6,
    reviewCount: 12800,
    address: 'Foz do Douro, 4150-572 Porto, Portugal',
    transportation: {
      bus: '500, 502, 1M, 1Z',
      tram: '1',
      note: 'About 30 minutes from downtown Porto by public transport'
    }
  }),

  // Faro POIs
  'old-town-faro': createPoi({
    id: 'old-town-faro',
    title: 'Old Town Faro',
    description: 'Charming historic center of Faro with cobblestone streets, whitewashed buildings, and a rich cultural heritage dating back to Roman times.',
    city: 'Faro',
    categories: ['historic', 'culture', 'walking'],
    lat: 37.0147,
    lng: -7.935,
    images: ['https://images.unsplash.com/photo-1580070142211-03f8c9f3d0c9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      website: 'https://www.visitalgarve.pt/en/130/faro.aspx'
    },
    hours: {
      alwaysOpen: true,
      note: 'Public area accessible 24/7; individual attractions have their own hours'
    },
    features: {
      wheelchairAccess: 'Partial',
      restrooms: 'Available at nearby cafes and restaurants'
    },
    tips: [
      'Visit the Arco da Vila for beautiful city views',
      'Explore the Sé Cathedral and its bone chapel',
      'Wander through the Jardim Manuel Bívar by the marina'
    ],
    bestTimeToVisit: ['morning', 'evening'],
    averageVisitDuration: 120,
    rating: 4.5,
    reviewCount: 9800,
    address: 'Largo da Sé, 8000-138 Faro, Portugal'
  }),

  'vila-adentro': createPoi({
    id: 'vila-adentro',
    title: 'Vila Adentro',
    description: 'Historic walled town center of Faro, home to traditional Algarve architecture, charming plazas, and authentic local restaurants.',
    city: 'Faro',
    categories: ['historic', 'food', 'culture'],
    lat: 37.0144,
    lng: -7.9322,
    images: ['https://images.unsplash.com/photo-1571008651748-8a9a1f2a466f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      website: 'https://www.visitalgarve.pt/en/130/faro.aspx'
    },
    hours: {
      alwaysOpen: true,
      note: 'Public area accessible 24/7; individual businesses have their own hours'
    },
    features: {
      wheelchairAccess: 'Partial',
      restrooms: 'Available at restaurants and cafes'
    },
    tips: [
      'Try cataplana, a traditional seafood dish',
      'Visit the Municipal Museum in the former Convent of Nossa Senhora da Assunção',
      'Explore the Arco do Repouso for great photo opportunities'
    ],
    bestTimeToVisit: ['lunch', 'dinner'],
    averageVisitDuration: 90,
    rating: 4.4,
    reviewCount: 7600,
    address: 'Vila Adentro, 8000-138 Faro, Portugal'
  }),

  'praia-de-faro': createPoi({
    id: 'praia-de-faro',
    title: 'Praia de Faro',
    description: 'Beautiful sandy beach on the Ilha de Faro, known for its calm waters and relaxed atmosphere, connected to the mainland by a bridge.',
    city: 'Faro',
    categories: ['beach', 'nature', 'relaxation'],
    lat: 36.9924,
    lng: -7.8509,
    images: ['https://images.unsplash.com/photo-1555396273-367ea4eb4db5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1374&q=80'],
    contact: {
      website: 'https://www.visitalgarve.pt/en/explorar/praias/praia-de-faro/305/'
    },
    hours: {
      alwaysOpen: true,
      note: 'Lifeguard service available in summer months'
    },
    features: {
      wheelchairAccess: true,
      restrooms: true,
      showers: true,
      lifeguard: 'Seasonal',
      parking: true
    },
    tips: [
      'Visit during the week for fewer crowds',
      'Try water sports like paddleboarding or kayaking',
      'Walk along the boardwalk for great views of the Ria Formosa'
    ],
    bestTimeToVisit: ['summer', 'weekday'],
    averageVisitDuration: 180,
    rating: 4.6,
    reviewCount: 12300,
    address: 'Praia de Faro, 8005-521 Faro, Portugal',
    transportation: {
      bus: '16, 18',
      note: 'About 15 minutes by car or 30 minutes by bus from Faro city center'
    }
  }),

  'faaron-steakhouse': createPoi({
    id: 'faaron-steakhouse',
    title: 'Faaron Steakhouse',
    description: 'Popular steakhouse in Faro known for its high-quality meats, cozy atmosphere, and excellent service, offering a wide selection of Portuguese and international dishes.',
    city: 'Faro',
    categories: ['restaurant', 'food', 'steakhouse'],
    lat: 37.018,
    lng: -7.9355,
    images: ['https://images.unsplash.com/photo-1555396273-367ea4eb4db5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1374&q=80'],
    contact: {
      phone: '+351 289 827 123',
      website: 'https://www.faaronsteakhouse.com',
      email: 'reservas@faaronsteakhouse.com'
    },
    hours: {
      monday: '12:00-15:00, 19:00-23:00',
      tuesday: '12:00-15:00, 19:00-23:00',
      wednesday: '12:00-15:00, 19:00-23:00',
      thursday: '12:00-15:00, 19:00-23:00',
      friday: '12:00-15:00, 19:00-23:00',
      saturday: '12:00-23:00',
      sunday: '12:00-23:00',
      note: 'Kitchen closes between lunch and dinner service'
    },
    price: {
      range: '€€€',
      currency: 'EUR',
      averageMeal: 30.00,
      note: 'Premium cuts and wine pairing options available'
    },
    features: {
      wheelchairAccess: true,
      reservations: true,
      restrooms: true,
      outdoorSeating: true
    },
    tips: [
      'Try their signature Black Angus ribeye',
      'Make reservations for dinner, especially on weekends',
      'Ask for wine pairing recommendations'
    ],
    bestTimeToVisit: ['dinner'],
    averageVisitDuration: 90,
    rating: 4.6,
    reviewCount: 3200,
    address: 'Rua de Santo António 22, 8000-386 Faro, Portugal'
  }),

  'bar-chessenta': createPoi({
    id: 'bar-chessenta',
    title: 'Bar CheSsenta',
    description: 'Vibrant bar in Faro known for its live music, creative cocktails, and lively atmosphere, popular with both locals and tourists.',
    city: 'Faro',
    categories: ['bar', 'nightlife', 'music'],
    lat: 37.0155,
    lng: -7.932,
    images: ['https://images.unsplash.com/photo-1571008651748-8a9a1f2a466f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      phone: '+351 289 827 124',
      facebook: 'https://www.facebook.com/barchessenta',
      instagram: '@barchessenta'
    },
    hours: {
      monday: '18:00-02:00',
      tuesday: '18:00-02:00',
      wednesday: '18:00-02:00',
      thursday: '18:00-02:00',
      friday: '18:00-04:00',
      saturday: '18:00-04:00',
      sunday: '18:00-02:00',
      note: 'Live music on weekends'
    },
    price: {
      range: '€€',
      currency: 'EUR',
      cocktail: 7.00,
      beer: 3.00
    },
    features: {
      liveMusic: true,
      outdoorSeating: true,
      wheelchairAccess: 'Limited'
    },
    tips: [
      'Arrive early on weekends to get a good spot',
      'Try their signature cocktails',
      'Check their social media for special events'
    ],
    bestTimeToVisit: ['evening', 'night'],
    averageVisitDuration: 120,
    rating: 4.5,
    reviewCount: 2800,
    address: 'Rua do Prior 60, 8000-257 Faro, Portugal'
  }),

  'ria-formosa-tour': createPoi({
    id: 'ria-formosa-tour',
    title: 'Ria Formosa Tour',
    description: 'Boat tours through the stunning Ria Formosa Natural Park, a protected lagoon system known for its diverse birdlife, salt pans, and beautiful barrier islands.',
    city: 'Faro',
    categories: ['nature', 'boat tour', 'wildlife'],
    lat: 36.9978,
    lng: -7.8205,
    images: ['https://images.unsplash.com/photo-1580070142211-03f8c9f3d0c9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      phone: '+351 289 817 343',
      website: 'https://www.formosamar.com',
      email: 'info@formosamar.com'
    },
    hours: {
      monday: '09:00-18:00',
      tuesday: '09:00-18:00',
      wednesday: '09:00-18:00',
      thursday: '09:00-18:00',
      friday: '09:00-18:00',
      saturday: '09:00-18:00',
      sunday: '09:00-18:00',
      note: 'Tour times vary by season; advance booking recommended'
    },
    price: {
      adult: 30.00,
      child: 15.00,
      family: 75.00,
      currency: 'EUR',
      note: 'Prices vary by tour type and duration'
    },
    features: {
      wheelchairAccess: 'Partial',
      restrooms: 'Available at departure point',
      duration: '2-4 hours',
      languages: ['English', 'Portuguese', 'Spanish', 'French']
    },
    tips: [
      'Bring sunscreen and a hat',
      'Binoculars are useful for birdwatching',
      'Book the sunset tour for spectacular views'
    ],
    bestTimeToVisit: ['morning', 'afternoon'],
    averageVisitDuration: 180,
    rating: 4.8,
    reviewCount: 4500,
    address: 'Doca de Faro, 8000-541 Faro, Portugal'
  }),

  'marisqueira-foz': createPoi({
    id: 'marisqueira-foz',
    title: 'Marisqueira Foz',
    description: 'Seaside restaurant specializing in fresh seafood and traditional Algarvian cuisine, with stunning views over the Ria Formosa Natural Park.',
    city: 'Faro',
    categories: ['restaurant', 'seafood', 'views'],
    lat: 36.998,
    lng: -7.821,
    images: ['https://images.unsplash.com/photo-1555396273-367ea4eb4db5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1374&q=80'],
    contact: {
      phone: '+351 289 817 630',
      website: 'http://www.marisqueirafoz.com',
      email: 'reservas@marisqueirafoz.com'
    },
    hours: {
      monday: '12:00-15:30, 19:00-23:00',
      tuesday: '12:00-15:30, 19:00-23:00',
      wednesday: '12:00-15:30, 19:00-23:00',
      thursday: '12:00-15:30, 19:00-23:00',
      friday: '12:00-15:30, 19:00-23:00',
      saturday: '12:00-23:00',
      sunday: '12:00-23:00',
      note: 'Kitchen closes between lunch and dinner service'
    },
    price: {
      range: '€€€',
      currency: 'EUR',
      averageMeal: 35.00,
      note: 'Seafood priced by weight; market price'
    },
    features: {
      waterfront: true,
      outdoorSeating: true,
      wheelchairAccess: true,
      reservations: true
    },
    tips: [
      'Try the cataplana de marisco (seafood stew)',
      'Ask for the catch of the day',
      'Reserve a table by the window for the best views'
    ],
    bestTimeToVisit: ['lunch', 'sunset'],
    averageVisitDuration: 90,
    rating: 4.7,
    reviewCount: 3800,
    address: 'Rua Infante Dom Henrique 68, 8000-363 Faro, Portugal'
  }),

  'ilha-deserta': createPoi({
    id: 'ilha-deserta',
    title: 'Ilha Deserta',
    description: 'Pristine barrier island known as "Deserted Island" with a beautiful, nearly untouched beach, part of the Ria Formosa Natural Park.',
    city: 'Faro',
    categories: ['beach', 'nature', 'hiking'],
    lat: 36.9995,
    lng: -7.851,
    images: ['https://images.unsplash.com/photo-1580070142211-03f8c9f3d0c9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      website: 'https://www.ilhadeserta.pt',
      phone: '+351 917 811 856'
    },
    hours: {
      alwaysOpen: true,
      note: 'Boat transfers operate from Faro harbor; last return varies by season'
    },
    features: {
      beach: true,
      natureTrails: true,
      restaurant: 'Seasonal',
      restrooms: 'Seasonal',
      lifeguard: 'Seasonal'
    },
    tips: [
      'Bring water and snacks as facilities are limited',
      'Wear comfortable shoes for walking on sand',
      'Visit the Estaminé restaurant for a meal with a view'
    ],
    bestTimeToVisit: ['summer', 'weekday'],
    averageVisitDuration: 240,
    rating: 4.8,
    reviewCount: 5200,
    address: 'Ilha Deserta, 8005-546 Faro, Portugal',
    transportation: {
      boat: 'Regular ferries from Faro harbor',
      duration: '45 minutes by boat',
      note: 'No cars allowed on the island'
    }
  }),

  'casa-do-pescador': createPoi({
    id: 'casa-do-pescador',
    title: 'Casa do Pescador',
    description: 'Charming family-run restaurant serving traditional Algarvian seafood dishes in a cozy, authentic setting near the waterfront.',
    city: 'Faro',
    categories: ['restaurant', 'seafood', 'local cuisine'],
    lat: 37.016,
    lng: -7.936,
    images: ['https://images.unsplash.com/photo-1571008651748-8a9a1f2a466f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      phone: '+351 289 824 567',
      email: 'reservas@casadopescador.pt'
    },
    hours: {
      monday: '12:00-15:00, 19:00-22:30',
      tuesday: '12:00-15:00, 19:00-22:30',
      wednesday: '12:00-15:00, 19:00-22:30',
      thursday: '12:00-15:00, 19:00-22:30',
      friday: '12:00-15:00, 19:00-22:30',
      saturday: '12:00-15:00, 19:00-22:30',
      sunday: '12:00-15:00',
      note: 'Closed Sunday evenings and all day Monday'
    },
    price: {
      range: '€€',
      currency: 'EUR',
      averageMeal: 25.00,
      note: 'Fresh seafood market price'
    },
    features: {
      outdoorSeating: true,
      reservations: true,
      wheelchairAccess: 'Limited',
      familyFriendly: true
    },
    tips: [
      'Try the grilled sardines or cataplana',
      'Ask for the catch of the day',
      'Portions are generous - consider sharing'
    ],
    bestTimeToVisit: ['lunch', 'dinner'],
    averageVisitDuration: 90,
    rating: 4.6,
    reviewCount: 2900,
    address: 'Rua do Compromisso 14, 8000-180 Faro, Portugal'
  }),

  'marina-walk': createPoi({
    id: 'marina-walk',
    title: 'Marina Walk',
    description: 'Picturesque waterfront promenade along Faro Marina, perfect for an evening stroll with views of boats and the Ria Formosa Natural Park.',
    city: 'Faro',
    categories: ['scenic', 'walking', 'views'],
    lat: 37.0158,
    lng: -7.9327,
    images: ['https://images.unsplash.com/photo-1580070142211-03f8c9f3d0c9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      website: 'https://www.visitalgarve.pt/en/130/faro.aspx'
    },
    hours: {
      alwaysOpen: true,
      note: 'Best visited at sunset for spectacular views'
    },
    features: {
      wheelchairAccess: true,
      benches: true,
      lighting: true
    },
    tips: [
      'Visit at sunset for the best photo opportunities',
      'Stop at one of the waterfront cafes for a drink',
      'Look for the traditional Portuguese fishing boats'
    ],
    bestTimeToVisit: ['sunset', 'evening'],
    averageVisitDuration: 60,
    rating: 4.5,
    reviewCount: 3400,
    address: 'Marina de Faro, 8000-541 Faro, Portugal'
  }),

  'igreja-do-carmo': createPoi({
    id: 'igreja-do-carmo',
    title: 'Igreja do Carmo',
    description: 'Baroque church famous for its bone chapel (Capela dos Ossos) adorned with the bones of over 1,200 monks.',
    city: 'Faro',
    categories: ['historic', 'religious', 'architecture'],
    lat: 37.0189,
    lng: -7.9296,
    images: ['https://images.unsplash.com/photo-1571008651748-8a9a1f2a466f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      phone: '+351 289 824 490',
      website: 'https://www.visitalgarve.pt/en/130/igreja-do-carmo-capela-dos-ossos.aspx'
    },
    hours: {
      monday: '10:00-13:00, 15:00-18:00',
      tuesday: '10:00-13:00, 15:00-18:00',
      wednesday: '10:00-13:00, 15:00-18:00',
      thursday: '10:00-13:00, 15:00-18:00',
      friday: '10:00-13:00, 15:00-18:00',
      saturday: '10:00-13:00, 15:00-18:00',
      sunday: '10:00-13:00',
      note: 'Closed on Sunday afternoons and religious holidays'
    },
    price: {
      adult: 2.00,
      child: 1.00,
      senior: 1.50,
      currency: 'EUR',
      note: 'Cash only'
    },
    features: {
      guidedTours: true,
      giftShop: true,
      photography: 'No flash photography inside the bone chapel'
    },
    tips: [
      'The bone chapel is small but worth the visit',
      'Be respectful as it is a religious site',
      'Combine with a visit to the nearby Municipal Museum'
    ],
    bestTimeToVisit: ['morning', 'afternoon'],
    averageVisitDuration: 45,
    rating: 4.3,
    reviewCount: 4100,
    address: 'Largo do Carmo 1, 8000-148 Faro, Portugal'
  }),

  'portas-sao-pedro': createPoi({
    id: 'portas-sao-pedro',
    title: 'Portas São Pedro',
    description: 'Historic city gate with a charming café inside the old city walls, offering a perfect spot to relax and enjoy the atmosphere.',
    city: 'Faro',
    categories: ['cafe', 'historic', 'views'],
    lat: 37.0175,
    lng: -7.9305,
    images: ['https://images.unsplash.com/photo-1555396273-367ea4eb4db5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1374&q=80'],
    contact: {
      phone: '+351 289 824 568',
      instagram: '@portassaopedro'
    },
    hours: {
      monday: '09:00-20:00',
      tuesday: '09:00-20:00',
      wednesday: '09:00-20:00',
      thursday: '09:00-20:00',
      friday: '09:00-22:00',
      saturday: '09:00-22:00',
      sunday: '10:00-18:00',
      note: 'Hours may vary by season'
    },
    price: {
      range: '€',
      currency: 'EUR',
      coffee: 1.20,
      pastry: 1.50,
      note: 'Affordable prices for the location'
    },
    features: {
      outdoorSeating: true,
      wifi: true,
      wheelchairAccess: 'Limited',
      restrooms: 'For customers only'
    },
    tips: [
      'Try the pastel de nata (custard tart)',
      'Great spot for people watching',
      'Ask for a table with a view of the square'
    ],
    bestTimeToVisit: ['morning', 'afternoon'],
    averageVisitDuration: 45,
    rating: 4.4,
    reviewCount: 1800,
    address: 'Largo de São Francisco 1, 8000-407 Faro, Portugal'
  }),

  // Albufeira POIs
  'praia-dos-pescadores': createPoi({
    id: 'praia-dos-pescadores',
    title: 'Praia dos Pescadores',
    description: 'Beautiful sandy beach in the heart of Albufeira, nestled between golden cliffs and offering crystal-clear waters and excellent facilities.',
    city: 'Albufeira',
    categories: ['beach', 'swimming', 'family'],
    lat: 37.0887,
    lng: -8.2503,
    images: ['https://images.unsplash.com/photo-1501785888041-af3ef285b470?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      website: 'https://www.visitalgarve.pt/en/explorar/praias/praia-dos-pescadores/297/'
    },
    hours: {
      alwaysOpen: true,
      note: 'Lifeguard service available from June to September, 10:00-18:00'
    },
    features: {
      lifeguard: 'Seasonal',
      showers: true,
      restrooms: true,
      sunbeds: 'For rent',
      waterSports: 'Available',
      wheelchairAccess: true
    },
    tips: [
      'Arrive early to secure a good spot during peak season',
      'Try the beachfront restaurants for fresh seafood',
      'Walk along the boardwalk to explore nearby beaches'
    ],
    bestTimeToVisit: ['morning', 'afternoon'],
    averageVisitDuration: 180,
    rating: 4.7,
    reviewCount: 15700,
    address: 'Praia dos Pescadores, 8200-635 Albufeira, Portugal'
  }),

  'old-town-albufeira': createPoi({
    id: 'old-town-albufeira',
    title: 'Old Town Albufeira',
    description: 'The heart of Albufeira, this enchanting historic district is a maze of whitewashed houses, cobblestone streets, and vibrant plazas. Once a small fishing village, the old town has maintained its traditional Algarve charm while offering modern amenities. The area comes alive with colorful buildings adorned with decorative tiles, lively bars, and restaurants serving fresh seafood.',
    city: 'Albufeira',
    categories: ['historic', 'shopping', 'food', 'culture'],
    lat: 37.0894,
    lng: -8.2508,
    images: [
      'https://images.pexels.com/photos/12228137/pexels-photo-12228137.jpeg?auto=compress&cs=tinysrgb&w=600',
      'https://images.unsplash.com/photo-1580070142211-03f8c9f3d0c9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'
    ],
    contact: {
      website: 'https://www.cm-albufeira.pt/en/turismo/centro-historico/centro-historico-de-albufeira/'
    },
    hours: {
      alwaysOpen: true,
      note: 'Best visited in the morning or late afternoon to avoid crowds. Evenings are magical with the lights.'
    },
    features: {
      walkingTours: 'Available in multiple languages',
      bestPhotoSpots: ['Clock Tower', 'Miradouro do Pau da Bandeira', 'Rua da Igreja Velha'],
      wheelchairAccess: 'Partial',
      restrooms: 'In restaurants and cafes',
      parking: 'Nearby paid parking available'
    },
    tips: [
      'Visit the Miradouro da Praia do Peneco for stunning views',
      'Explore the local craft shops for souvenirs',
      'Try the traditional cataplana dish in one of the local restaurants'
    ],
    bestTimeToVisit: ['morning', 'evening'],
    averageVisitDuration: 120,
    rating: 4.6,
    reviewCount: 14200,
    address: 'Centro Histórico, 8200-001 Albufeira, Portugal'
  }),

  'zoomarine': createPoi({
    id: 'zoomarine',
    title: 'Zoomarine',
    description: 'Portugal\'s premier marine life park, Zoomarine offers an unforgettable day of entertainment and education for the whole family. Experience thrilling dolphin and sea lion shows, exciting water slides, and up-close encounters with marine animals. The park combines conservation education with fun attractions, including tropical beaches, a 4D cinema, and a variety of dining options.',
    city: 'Albufeira',
    categories: ['family', 'animals', 'water park', 'attraction'],
    lat: 37.0936,
    lng: -8.3333,
    images: [
      'https://images.pexels.com/photos/225869/pexels-photo-225869.jpeg?auto=compress&cs=tinysrgb&w=600',
      'https://images.unsplash.com/photo-1534536282915-9a8cb0a5941a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'
    ],
    contact: {
      phone: '+351 289 560 300',
      website: 'https://www.zoomarine.pt/en/',
      email: 'info@zoomarine.pt',
      address: 'N125, 8201-864 Guia, Portugal'
    },
    hours: {
      monday: '10:00-17:30',
      tuesday: '10:00-17:30',
      wednesday: '10:00-17:30',
      thursday: '10:00-17:30',
      friday: '10:00-17:30',
      saturday: '10:00-17:30',
      sunday: '10:00-17:30',
      note: 'Summer hours (July-August) typically 10:00-19:30. Winter hours may vary. Check website for current schedule and show times.'
    },
    price: {
      adult: 36.00,
      child: 26.00,
      senior: 27.00,
      family: 112.00,
      currency: 'EUR',
      note: 'Discounted tickets available online. Special packages include swimming with dolphins. Parking is free.'
    },
    features: {
      shows: ['Dolphin Bay', 'Sealions Harbor', 'Tropical Birds', '4D Cinema'],
      attractions: ['Water Slides', 'Ferris Wheel', 'Carousel', 'Wave Pool'],
      dining: ['Self-service restaurants', 'Snack bars', 'Ice cream parlors'],
      accessibility: 'Wheelchair accessible with special viewing areas',
      parking: 'Free parking available',
      lockers: 'Available for rent',
      giftShop: 'Available on-site',
      firstAid: true
    },
    tips: [
      'Arrive early to see the dolphin shows',
      'Bring swimwear for the water park area',
      'Download the park map and show schedule in advance'
    ],
    bestTimeToVisit: ['weekday', 'morning'],
    averageVisitDuration: 300,
    rating: 4.5,
    reviewCount: 23500,
    address: 'N125, km 65, 8201-864 Guia, Portugal',
    transportation: {
      bus: 'From Albufeira to Guia, then short taxi ride',
      shuttle: 'Available from major hotels in the area',
      parking: 'Available on-site for a fee'
    }
  }),

  'sao-sebastiao-church': createPoi({
    id: 'sao-sebastiao-church',
    title: 'São Sebastião Church',
    description: '16th-century church featuring beautiful azulejo tiles and a mix of architectural styles, including Manueline and Baroque elements.',
    city: 'Albufeira',
    categories: ['historic', 'religious', 'architecture'],
    lat: 37.0892,
    lng: -8.2505,
    images: ['https://images.unsplash.com/photo-1571008651748-8a9a1f2a466f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      phone: '+351 289 585 526',
      website: 'https://www.cm-albufeira.pt/en/turismo/patrimonio/igreja-matriz-de-albufeira/'
    },
    hours: {
      monday: '09:30-12:30, 14:00-17:00',
      tuesday: '09:30-12:30, 14:00-17:00',
      wednesday: '09:30-12:30, 14:00-17:00',
      thursday: '09:30-12:30, 14:00-17:00',
      friday: '09:30-12:30, 14:00-17:00',
      saturday: '09:30-12:30',
      sunday: 'Closed',
      note: 'Hours may vary for religious services'
    },
    price: {
      admission: 'Free',
      donation: 'Appreciated',
      currency: 'EUR'
    },
    features: {
      wheelchairAccess: 'Partial',
      photography: 'Allowed without flash',
      guidedTours: 'By appointment'
    },
    tips: [
      'Admire the beautiful azulejo tiles inside',
      'Check out the small museum area',
      'Visit during a service to hear the church organ'
    ],
    bestTimeToVisit: ['morning'],
    averageVisitDuration: 30,
    rating: 4.3,
    reviewCount: 2800,
    address: 'Rua da Igreja Nova, 8200-141 Albufeira, Portugal',
    nearby: 'Located in the heart of Old Town Albufeira, near the main square'
  }),

  'praia-da-falesia': createPoi({
    id: 'praia-da-falesia',
    title: 'Praia da Falésia',
    description: 'One of the most breathtaking beaches in the Algarve, Praia da Falésia is renowned for its dramatic red and golden cliffs that stretch for 6 kilometers along the coastline. The contrast between the vibrant cliffs, golden sand, and turquoise waters creates a stunning natural landscape. This Blue Flag beach offers excellent facilities while maintaining its natural beauty, making it perfect for swimming, sunbathing, and photography.',
    city: 'Albufeira',
    categories: ['beach', 'nature', 'photography', 'hiking'],
    lat: 37.0833,
    lng: -8.1833,
    images: [
      'https://images.pexels.com/photos/31617430/pexels-photo-31617430/free-photo-of-stunning-coastal-cliffs-in-paraiba-brazil.jpeg?auto=compress&cs=tinysrgb&w=600',
      'https://images.unsplash.com/photo-1501785888041-af3ef285b470?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      'https://images.unsplash.com/photo-1583619430797-8c8d84541ad1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'
    ],
    contact: {
      website: 'https://www.visitalgarve.pt/en/explorar/praias/praia-da-falesia/300/'
    },
    hours: {
      alwaysOpen: true,
      note: 'Lifeguard service available June-September (10:00-18:00)'
    },
    price: {
      entry: 'Free',
      sunbed: '€15-20 per day',
      umbrella: '€10-15 per day',
      parking: '€3-6 per hour',
      currency: 'EUR',
      note: 'Beach facilities operate seasonally (April-October)'
    },
    features: {
      length: '6 km',
      lifeguard: 'Seasonal (June-September)',
      facilities: ['Showers', 'Toilets', 'Beach bars', 'Restaurants', 'First aid'],
      activities: ['Swimming', 'Beach walks', 'Cliff hiking', 'Photography', 'Beach volleyball'],
      accessibility: 'Wooden walkways to the beach, but cliff areas are not wheelchair accessible',
      waterQuality: 'Excellent, Blue Flag certified',
      wildlife: 'Home to various bird species and marine life'
    },
    tips: [
      'Visit early in the morning to secure a good spot and avoid crowds',
      'Wear comfortable shoes for the walk down to the beach and along the cliffs',
      'The cliffs provide natural shade in the afternoon',
      'Bring water and sun protection as the beach can get very hot',
      'Don\'t miss the sunset - the cliffs glow beautifully in the evening light',
      'Consider a boat tour to see the cliffs from the water'
    ],
    bestTimeToVisit: ['morning', 'sunset'],
    averageVisitDuration: 240,
    rating: 4.8,
    reviewCount: 18400,
    lastUpdated: '2024-06-06',
    address: 'Praia da Falésia, 8200-593 Albufeira, Portugal',
    transportation: {
      bus: 'From Albufeira to Olhos de Água, then 15-minute walk',
      parking: 'Available at nearby hotels and public lots (€3-6/hour)',
      taxi: 'Approx. €10-15 from Albufeira center',
      walking: 'Scenic 45-minute walk from Albufeira marina along the coast'
    }
  }),

  'the-strip': createPoi({
    id: 'the-strip',
    title: 'The Strip',
    description: 'Vibrant nightlife area in Albufeira known for its numerous bars, clubs, and restaurants, offering entertainment into the early hours.',
    city: 'Albufeira',
    categories: ['nightlife', 'bars', 'restaurants'],
    lat: 37.1244,
    lng: -8.2778,
    images: ['https://images.unsplash.com/photo-1555396273-367ea4eb4db5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1374&q=80'],
    contact: {
      website: 'https://www.visitalgarve.pt/en/explorar/albufeira/rua-dos-bares/'
    },
    hours: {
      monday: '21:00-04:00',
      tuesday: '21:00-04:00',
      wednesday: '21:00-04:00',
      thursday: '21:00-04:00',
      friday: '21:00-06:00',
      saturday: '21:00-06:00',
      sunday: '21:00-04:00',
      note: 'Individual venues may have different hours; busiest in summer months'
    },
    price: {
      beer: '€2-5',
      cocktail: '€5-10',
      shots: '€3-8',
      currency: 'EUR',
      note: 'Many bars offer happy hour specials and drink promotions'
    },
    features: {
      liveMusic: true,
      karaoke: true,
      sportsBars: true,
      danceClubs: true,
      outdoorSeating: true
    },
    tips: [
      'Visit during happy hour for drink specials',
      'Try the local firewater, "medronho"',
      'Be aware of your belongings in crowded areas',
      'Consider taking a taxi back to your accommodation'
    ],
    bestTimeToVisit: ['night'],
    averageVisitDuration: 180,
    rating: 4.2,
    reviewCount: 12500,
    address: 'Rua do Município, 8200-001 Albufeira, Portugal',
    transportation: {
      taxi: 'Readily available',
      bus: 'Regular service from Albufeira center',
      parking: 'Limited; better to use public transport or taxis'
    }
  }),

  'albufeira-marina': createPoi({
    id: 'albufeira-marina',
    title: 'Albufeira Marina',
    description: 'Modern marina offering boat tours, water sports, and a variety of restaurants and shops in a scenic waterfront setting.',
    city: 'Albufeira',
    categories: ['marina', 'boat tours', 'dining'],
    lat: 37.0606,
    lng: -8.2625,
    images: ['https://images.unsplash.com/photo-1580070142211-03f8c9f3d0c9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      phone: '+351 289 591 878',
      website: 'https://www.marinadealbufeira.com',
      email: 'info@marinadealbufeira.com'
    },
    hours: {
      monday: '09:00-20:00',
      tuesday: '09:00-20:00',
      wednesday: '09:00-20:00',
      thursday: '09:00-20:00',
      friday: '09:00-22:00',
      saturday: '09:00-22:00',
      sunday: '09:00-20:00',
      note: 'Individual businesses may have different hours'
    },
    features: {
      boatTours: true,
      waterSports: true,
      restaurants: true,
      shops: true,
      parking: true,
      wheelchairAccess: true
    },
    tips: [
      'Book boat tours in advance during peak season',
      'Try the fresh seafood at the waterfront restaurants',
      'Visit the small beach next to the marina',
      'Great spot for sunset views'
    ],
    bestTimeToVisit: ['afternoon', 'evening'],
    averageVisitDuration: 120,
    rating: 4.5,
    reviewCount: 9800,
    address: 'Marina de Albufeira, 8200-394 Albufeira, Portugal',
    transportation: {
      bus: 'From Albufeira center to Marina de Albufeira',
      taxi: 'Available throughout the area',
      parking: 'Large parking lot available for a fee'
    }
  }),

  // Lagos POIs
  'ponta-da-piedade': createPoi({
    id: 'ponta-da-piedade',
    title: 'Ponta da Piedade',
    description: 'One of the most spectacular natural landmarks in Portugal, Ponta da Piedade is a dramatic coastline featuring towering golden cliffs, sea caves, and rock arches carved by the Atlantic Ocean. The name translates to "Point of Piety" and the site is renowned for its stunning rock formations that rise majestically from crystal-clear turquoise waters. This natural wonder offers some of the most breathtaking coastal views in the Algarve and is a must-visit for nature lovers and photographers alike.',
    city: 'Lagos',
    categories: ['nature', 'photography', 'boat tour', 'viewpoint', 'hiking'],
    lat: 37.0792,
    lng: -8.6708,
    images: [
      'https://images.pexels.com/photos/7877275/pexels-photo-7877275.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      'https://images.unsplash.com/photo-1605559424869-85fe1d0e8c2b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      'https://images.unsplash.com/photo-1605030753296-4d9a8d9b8e1c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'
    ],
    contact: {
      website: 'https://www.visitalgarve.pt/en/explorar/pontos-interesse/ponta-da-piedade/'
    },
    hours: {
      alwaysOpen: true,
      note: 'Best visited during daylight hours. Boat tours typically operate from 9:00 AM to 6:00 PM (April-October)'
    },
    price: {
      entry: 'Free',
      boatTours: '€25-40 per person',
      kayakTours: '€35-50 per person',
      parking: '€2-3 per hour',
      currency: 'EUR',
      note: 'Prices may vary by season and tour operator'
    },
    features: {
      boatTours: 'Available to explore sea caves and grottoes',
      kayakRentals: 'Guided kayak tours available',
      hikingTrails: 'Clifftop trails with stunning viewpoints',
      photographySpots: 'Numerous scenic viewpoints',
      accessibility: 'Limited to viewpoints only (not wheelchair accessible to the water)',
      facilities: ['Viewing platforms', 'Restaurant nearby', 'Gift shop', 'Restrooms'],
      wildlife: 'Home to various seabirds and marine life',
      geologicalFeatures: 'Sea stacks, arches, and caves formed by erosion'
    },
    tips: [
      'Take a boat tour at sunrise or sunset for the most dramatic lighting',
      'Wear comfortable, non-slip shoes for the wooden walkways and stairs',
      'Bring a hat, sunscreen, and plenty of water as shade is limited',
      'For the best photos, visit early morning when the light is soft and crowds are smaller',
      'Consider a kayak tour for a more intimate experience of the caves',
      'Check tide times as some caves are only accessible at certain tides',
      'Stay on marked paths as the cliffs can be dangerous'
    ],
    bestTimeToVisit: ['sunrise', 'morning', 'sunset'],
    averageVisitDuration: 180,
    rating: 4.9,
    reviewCount: 27500,
    lastUpdated: '2024-06-06',
    address: 'Ponta da Piedade, 8600-544 Lagos, Portugal',
    transportation: {
      bus: 'Route 2 from Lagos city center to Porto de Mós (15 min walk from stop)',
      taxi: 'Approx. €8-12 from Lagos center (10 min ride)',
      parking: 'Paid parking available near the lighthouse (€2-3/hour)',
      walking: 'Pleasant 45-minute coastal walk from Lagos marina',
      bike: 'Bike rental available in Lagos (20 min ride)'
    }
  }),

  'praia-dona-ana': createPoi({
    id: 'praia-dona-ana',
    title: 'Praia Dona Ana',
    description: 'Frequently ranked among the most beautiful beaches in the world, Praia Dona Ana is a breathtaking cove surrounded by dramatic golden cliffs and crystal-clear turquoise waters. The beach is famous for its striking rock formations, hidden grottoes, and the stunning contrast between the golden cliffs and the deep blue Atlantic. The calm, clear waters make it ideal for swimming and snorkeling, while the surrounding cliffs provide spectacular viewpoints and photo opportunities.',
    city: 'Lagos',
    categories: ['beach', 'swimming', 'photography', 'snorkeling', 'nature'],
    lat: 37.0875,
    lng: -8.6703,
    images: [
      'https://images.pexels.com/photos/30150456/pexels-photo-30150456/free-photo-of-scenic-aerial-view-of-lagos-beach-in-portugal.jpeg?auto=compress&cs=tinysrgb&w=600',
      'https://images.unsplash.com/photo-1501785888041-af3ef285b470?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      'https://images.unsplash.com/photo-1583619430797-8c8d84541ad1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'
    ],
    contact: {
      website: 'https://www.visitalgarve.pt/en/explorar/praias/praia-de-d-ana/'
    },
    hours: {
      alwaysOpen: true,
      note: 'Lifeguard service available June-September (10:00-18:00)'
    },
    price: {
      entry: 'Free',
      sunbed: '€15-20 per day',
      umbrella: '€10-15 per day',
      parking: '€2-3 per hour',
      currency: 'EUR',
      note: 'Beach facilities operate seasonally (April-October)'
    },
    features: {
      lifeguard: 'Seasonal (June-September)',
      facilities: ['Showers', 'Toilets', 'Beach bar', 'Restaurant', 'First aid'],
      activities: ['Swimming', 'Snorkeling', 'Beach walks', 'Cliff jumping (for the adventurous)'],
      accessibility: 'Many stairs to access the beach (not wheelchair accessible)',
      waterQuality: 'Excellent, Blue Flag certified',
      geology: 'Dramatic sandstone cliffs with unique rock formations',
      bestPhotoSpots: ['Clifftop viewpoints', 'Rock formations at low tide', 'Sunset from the western cliffs']
    },
    tips: [
      'Visit early in the morning to avoid crowds and secure a good spot',
      'Bring water shoes as the sand can get extremely hot',
      'Explore the rock formations and small caves at low tide',
      'For the best photos, visit during golden hour before sunset',
      'Consider a boat tour to see the beach from the water',
      'Bring your own umbrella if visiting outside peak season when rentals may be limited',
      'Stay hydrated and use sun protection as shade is limited'
    ],
    bestTimeToVisit: ['morning', 'late afternoon'],
    averageVisitDuration: 180,
    rating: 4.8,
    reviewCount: 22500,
    lastUpdated: '2024-06-06',
    address: 'Praia Dona Ana, 8600-500 Lagos, Portugal',
    transportation: {
      bus: 'Route 4 from Lagos city center (10 min ride, then 10 min walk)',
      taxi: 'Approx. €5-8 from Lagos center (5 min ride)',
      parking: 'Limited paid parking available (€2-3/hour)',
      walking: '20-minute walk from Lagos city center'
    }
  }),

  'meia-praia': createPoi({
    id: 'meia-praia',
    title: 'Meia Praia',
    description: 'Vast sandy beach stretching for 4km along the coast, offering plenty of space and excellent water sports opportunities.',
    city: 'Lagos',
    categories: ['beach', 'water sports', 'family'],
    lat: 37.1083,
    lng: -8.6583,
    images: ['https://images.unsplash.com/photo-1501785888041-af3ef285b470?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      website: 'https://www.visitalgarve.pt/en/explorar/praias/meia-praia/'
    },
    hours: {
      alwaysOpen: true,
      note: 'Lifeguard service available in summer months'
    },
    features: {
      length: '4 km',
      lifeguard: 'Seasonal',
      showers: true,
      restrooms: true,
      sunbeds: 'For rent',
      waterSports: 'Extensive options',
      beachBars: true,
      wheelchairAccess: 'Partial',
      parking: 'Available',
      waterQuality: 'Excellent'
    },
    tips: [
      'Great for long walks along the shore',
      'Try windsurfing or kitesurfing with local schools',
      'Visit the beach bars for fresh seafood and drinks',
      'Less crowded at the western end'
    ],
    bestTimeToVisit: ['morning', 'afternoon'],
    averageVisitDuration: 240,
    rating: 4.6,
    reviewCount: 15600,
    address: 'Meia Praia, 8600-315 Lagos, Portugal',
    transportation: {
      bus: 'Regular service from Lagos city center',
      taxi: 'Available throughout Lagos',
      parking: 'Multiple parking areas along the beach',
      walking: '25-minute walk from Lagos city center'
    }
  }),

  'lagos-zoo': createPoi({
    id: 'lagos-zoo',
    title: 'Lagos Zoo',
    description: 'Small but charming zoo with a variety of animals, beautiful gardens, and a strong focus on conservation and education.',
    city: 'Lagos',
    categories: ['family', 'animals', 'nature'],
    lat: 37.1333,
    lng: -8.7,
    images: ['https://images.unsplash.com/photo-1534536282915-9a8cb0a5941a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      phone: '+351 282 680 100',
      website: 'https://www.zoolagos.com',
      email: 'info@zoolagos.com'
    },
    hours: {
      january: '10:00-17:00',
      february: '10:00-17:00',
      march: '10:00-18:00',
      april: '10:00-19:00',
      may: '10:00-19:00',
      june: '10:00-20:00',
      july: '10:00-20:00',
      august: '10:00-20:00',
      september: '10:00-19:00',
      october: '10:00-18:00',
      november: '10:00-17:00',
      december: '10:00-17:00',
      note: 'Last entry 1 hour before closing'
    },
    price: {
      adult: 20.00,
      child: '14.00 (ages 3-11)',
      senior: '16.00 (65+)',
      family: '60.00 (2 adults + 2 children)',
      currency: 'EUR',
      note: 'Online discounts available; free for children under 3'
    },
    features: {
      wheelchairAccess: true,
      restaurant: true,
      picnicArea: true,
      giftShop: true,
      playground: true,
      animalFeedings: 'Daily schedule available',
      guidedTours: 'Available upon request'
    },
    tips: [
      'Check the schedule for animal feedings and shows',
      'Allow 2-3 hours for a complete visit',
      'Bring a hat and sunscreen as most areas are outdoors',
      'Visit in the morning when animals are most active'
    ],
    bestTimeToVisit: ['weekday', 'morning'],
    averageVisitDuration: 180,
    rating: 4.4,
    reviewCount: 8700,
    address: 'Estrada de Bensafrim, 8600-013 Lagos, Portugal',
    transportation: {
      bus: 'From Lagos city center to Lagos Zoo',
      taxi: 'Available throughout Lagos',
      parking: 'Free parking available on site',
      walking: '30-minute walk from Lagos city center'
    }
  }),

  'fort-of-ponta-da-bandeira': createPoi({
    id: 'fort-of-ponta-da-bandeira',
    title: 'Fort of Ponta da Bandeira',
    description: '17th-century fort with beautiful views over the ocean, featuring a small chapel with 18th-century tiles and a maritime exhibition.',
    city: 'Lagos',
    categories: ['historic', 'museum', 'viewpoint'],
    lat: 37.1014,
    lng: -8.6686,
    images: ['https://images.unsplash.com/photo-1571008651748-8a9a1f2a466f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      phone: '+351 282 762 301',
      website: 'https://www.cm-lagos.pt/equipamento/150/forte-ponta-da-bandeira',
      email: 'fortepontadabandeira@cm-lagos.pt'
    },
    hours: {
      octoberToApril: '09:30-17:00',
      mayToSeptember: '09:30-18:30',
      closed: 'December 25, January 1',
      note: 'Last entry 30 minutes before closing'
    },
    price: {
      adult: 3.00,
      child: '1.50 (ages 6-12)',
      senior: '1.50 (65+)',
      family: '7.50 (2 adults + 2 children)',
      student: '2.00',
      currency: 'EUR',
      note: 'Free admission on the first Sunday of each month for residents in Portugal'
    },
    features: {
      wheelchairAccess: 'Partial',
      guidedTours: 'Available in multiple languages',
      giftShop: true,
      restrooms: true,
      photography: 'Allowed without flash',
      historicalExhibits: true
    },
    tips: [
      'Climb to the rooftop for panoramic views of the coastline',
      'Visit the small but interesting maritime exhibition',
      'Check out the well-preserved drawbridge and moat',
      'Combine your visit with a walk along the nearby promenade'
    ],
    bestTimeToVisit: ['morning', 'late afternoon'],
    averageVisitDuration: 60,
    rating: 4.3,
    reviewCount: 6500,
    address: 'Forte da Ponta da Bandeira, 8600-645 Lagos, Portugal',
    nearbyAttractions: [
      'Praia da Batata (5 min walk)',
      'Lagos Marina (10 min walk)',
      'Ponte Romana (8 min walk)'
    ],
    transportation: {
      bus: 'From Lagos city center to Forte da Bandeira',
      taxi: 'Available throughout Lagos',
      parking: 'Limited street parking available',
      walking: '15-minute walk from Lagos city center'
    }
  }),

  // Sagres POIs
  'cabo-de-sao-vicente': createPoi({
    id: 'cabo-de-sao-vicente',
    title: 'Cabo de São Vicente',
    description: 'Standing proudly at the southwesternmost point of continental Europe, Cabo de São Vicente is a place of raw natural beauty and historical significance. Known as "The End of the World" in Roman times, these dramatic cliffs rise 75 meters above the Atlantic Ocean, offering some of the most spectacular coastal views in Portugal. The iconic lighthouse, one of the most powerful in Europe, has guided sailors since 1846 and stands as a sentinel at this windswept, mystical location that has been a sacred site for thousands of years.',
    city: 'Sagres',
    categories: ['nature', 'viewpoint', 'historic', 'lighthouse', 'sunset'],
    lat: 37.0233,
    lng: -8.9969,
    images: [
      'https://images.pexels.com/photos/7868988/pexels-photo-7868988.jpeg?auto=compress&cs=tinysrgb&w=600',
      'https://images.unsplash.com/photo-1605559424869-85fe1d0e8c2b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      'https://images.unsplash.com/photo-1583619430797-8c8d84541ad1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'
    ],
    contact: {
      website: 'https://www.visitalgarve.pt/en/explorar/pontos-interesse/cabo-de-sao-vicente/',
      phone: '+351 282 620 140'
    },
    hours: {
      lighthouse: {
        octoberToMarch: '10:00-17:30 (last entry 17:00)',
        aprilToSeptember: '09:30-19:30 (last entry 19:00)',
        closed: 'January 1, December 25',
        note: 'Museum and lighthouse tower have guided tours every 30 minutes'
      },
      alwaysOpen: true,
      bestViewingTimes: 'Sunrise and sunset offer the most dramatic lighting'
    },
    price: {
      lighthouseMuseum: '3.00',
      childrenUnder12: 'Free',
      seniorsOver65: '1.50',
      familyTicket: '7.50',
      exteriorAreas: 'Free',
      currency: 'EUR',
      note: 'Free admission on the first Sunday of each month for residents in Portugal'
    },
    features: {
      viewpoint: 'Multiple panoramic viewpoints along the cliffs',
      giftShop: 'Sells local crafts and souvenirs',
      cafe: 'Small café with light refreshments',
      restrooms: 'Available near the lighthouse',
      parking: 'Large free parking area',
      wheelchairAccess: 'Partial (exterior areas and ground floor of visitor center only)',
      facilities: ['Information panels', 'Binocular stations', 'Picnic area'],
      wildlife: 'Excellent spot for birdwatching (especially during migration seasons)'
    },
    tips: [
      'Visit at sunset for one of the most spectacular views in Portugal',
      'Bring a windbreaker or jacket as it can be extremely windy',
      'Wear sturdy shoes if planning to walk along the cliff paths',
      'Arrive early to secure parking during peak season',
      'Check the weather forecast as fog can reduce visibility',
      'Combine your visit with nearby Fortaleza de Sagres for a full day trip',
      'The lighthouse museum offers fascinating insights into maritime history',
      'Stay for the sunset but bring a flashlight for the walk back to the parking lot'
    ],
    bestTimeToVisit: ['sunset', 'sunrise', 'morning'],
    averageVisitDuration: 120,
    rating: 4.9,
    reviewCount: 23500,
    lastUpdated: '2024-06-06',
    address: 'Cabo de São Vicente, 8650-390 Sagres, Portugal',
    transportation: {
      bus: 'From Lagos to Sagres (1 hour), then taxi to the cape (15 min, approx. €15-20)',
      car: 'Free parking available (15 min drive from Sagres, 45 min from Lagos)',
      taxi: 'Available from Sagres town (15 min ride, approx. €15-20)',
      bike: 'Scenic 20km ride from Sagres (challenging due to wind)',
      organizedTour: 'Available from major Algarve resorts'
    },
    nearbyAttractions: [
      'Fortaleza de Sagres (15 min drive)',
      'Praia do Beliche (10 min drive)'
    ]
  }),

  'fortaleza-de-sagres': createPoi({
    id: 'fortaleza-de-sagres',
    title: 'Fortaleza de Sagres',
    description: 'Perched dramatically on a windswept promontory at the southwestern tip of Europe, Fortaleza de Sagres is a monumental fortress that played a crucial role in Portugal\'s Age of Discoveries. This vast, star-shaped fortress offers breathtaking 360-degree views of the Atlantic Ocean and the rugged Algarve coastline. The site includes the famous Rosa dos Ventos (Wind Rose), a 43-meter diameter stone compass believed to be used by navigators, and the historic Church of Nossa Senhora da Graça, built on the orders of Prince Henry the Navigator in the 15th century.',
    city: 'Sagres',
    categories: ['historic', 'viewpoint', 'architecture', 'cultural', 'landmark'],
    lat: 36.9911,
    lng: -8.9475,
    images: [
      'https://images.pexels.com/photos/7868997/pexels-photo-7868997.jpeg?auto=compress&cs=tinysrgb&w=600',
      'https://images.unsplash.com/photo-1571008651748-8a9a1f2a466f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      'https://images.unsplash.com/photo-1583619430797-8c8d84541ad1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'
    ],
    contact: {
      phone: '+351 282 620 140',
      website: 'https://www.cultalg.pt/patrimonio/fortaleza-de-sagres/',
      email: 'fortaleza.sagres@cultalg.pt'
    },
    hours: {
      mayToSeptember: '09:30-20:00 (last entry 19:30)',
      octoberToApril: '09:30-17:30 (last entry 17:00)',
      closed: 'January 1, May 1, December 25',
      bestTimeToVisit: 'Early morning or late afternoon to avoid crowds and heat'
    },
    price: {
      adult: 6.00,
      child: 'Free (under 12)',
      senior: '3.00 (65+)',
      student: '3.00 (with valid ID)',
      family: '15.00 (2 adults + 2 children)',
      audioGuide: '2.00',
      currency: 'EUR',
      note: 'Free admission on Sundays and public holidays until 14:00 for residents in Portugal'
    },
    features: {
      audioGuide: 'Available in Portuguese, English, Spanish, French, and German',
      giftShop: 'Sells books, souvenirs, and local crafts',
      cafe: 'Small café with light refreshments and ocean views',
      restrooms: 'Available near the entrance',
      parking: 'Large free parking area',
      facilities: ['Information center', 'Exhibition space', 'Picnic area'],
      accessibility: 'Partially accessible (some areas have steps and uneven surfaces)',
      photography: 'Allowed throughout the site (tripods may require permission)'
    },
    tips: [
      'Wear comfortable walking shoes as the site is large with uneven surfaces',
      'Bring sun protection (hat, sunscreen) as shade is limited',
      'Allow at least 2 hours to fully explore the fortress and enjoy the views',
      'Visit the Rosa dos Ventos early in the morning for the best lighting',
      'Check the weather forecast as the site can be very windy',
      'Combine your visit with nearby Cabo de São Vicente for a full day trip',
      'The fortress can be quite crowded during peak season (July-August)',
      'Bring water, especially during summer months'
    ],
    bestTimeToVisit: ['morning', 'late afternoon'],
    averageVisitDuration: 120,
    rating: 4.8,
    reviewCount: 19500,
    lastUpdated: '2024-06-06',
    address: 'Fortaleza de Sagres, 8650-360 Sagres, Portugal',
    transportation: {
      bus: 'From Lagos to Sagres center (1 hour), then 15-minute walk or short taxi ride',
      car: 'Free parking available (5 min drive from Sagres center)',
      taxi: 'Available from Sagres town center (5 min ride, approx. €5-8)',
      walking: 'Pleasant 15-20 minute walk from Sagres town center'
    },
    historicalSignificance: 'The fortress was a key location during Portugal\'s Age of Discoveries in the 15th century and is closely associated with Prince Henry the Navigator, who established a school of navigation here. The site was crucial for the development of maritime exploration that led to the discovery of new trade routes to Africa, India, and Brazil.'
  }),

  'praia-do-martinhal': createPoi({
    id: 'praia-do-martinhal',
    title: 'Praia do Martinhal',
    description: 'Beautiful family-friendly beach with calm, shallow waters, surrounded by sand dunes and offering excellent water sports facilities.',
    city: 'Sagres',
    categories: ['beach', 'family', 'water sports'],
    lat: 37.0167,
    lng: -8.9333,
    images: ['https://images.unsplash.com/photo-1501785888041-af3ef285b470?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      website: 'https://www.visitalgarve.pt/en/explorar/praias/praia-do-martinhal/'
    },
    hours: {
      alwaysOpen: true,
      note: 'Lifeguard service available in summer months (June to September)'
    },
    features: {
      length: '1.2 km',
      lifeguard: 'Seasonal',
      showers: true,
      restrooms: true,
      sunbeds: 'For rent',
      waterSports: 'Extensive options',
      beachRestaurants: true,
      wheelchairAccess: 'Partial',
      parking: true,
      waterQuality: 'Excellent',
      sandType: 'Golden sand',
      waves: 'Gentle',
      wind: 'Sheltered from northern winds',
      facilities: ['Beach bar', 'Restaurants', 'Water sports rentals', 'First aid']
    },
    tips: [
      'Ideal for families with small children due to calm waters',
      'Try stand-up paddleboarding or kayaking',
      'Visit the nearby Martinhal Resort for additional dining options',
      'Great spot for beginner surfers',
      'Bring beach shoes as the sand can get hot'
    ],
    bestTimeToVisit: ['morning', 'afternoon'],
    averageVisitDuration: 180,
    rating: 4.6,
    reviewCount: 12400,
    address: 'Praia do Martinhal, 8650-357 Sagres, Portugal',
    transportation: {
      car: 'Parking available near the beach',
      bus: 'From Sagres town center to Martinhal',
      taxi: 'Available throughout Sagres',
      walking: '30-minute walk from Sagres town center'
    },
    nearbyAttractions: [
      'Fortaleza de Sagres (5 min drive)',
      'Praia da Mareta (10 min drive)',
      'Cabo de São Vicente (15 min drive)'
    ],
    waterSports: {
      surfing: 'Beginner to intermediate',
      windsurfing: 'Good conditions',
      kitesurfing: 'Popular spot',
      kayaking: 'Available for rent',
      paddleBoarding: 'Ideal conditions',
      schools: ['Sagres Natura Surf School', 'Sagres Adventure']
    }
  }),

  'praia-do-beliche': createPoi({
    id: 'praia-do-beliche',
    title: 'Praia do Beliche',
    description: 'Secluded beach surrounded by high cliffs, offering dramatic scenery and excellent surfing conditions.',
    city: 'Sagres',
    categories: ['beach', 'surfing', 'nature'],
    lat: 37.0167,
    lng: -8.95,
    images: ['https://images.unsplash.com/photo-1501785888041-af3ef285b470?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      website: 'https://www.visitalgarve.pt/en/explorar/praias/praia-do-beliche/'
    },
    hours: {
      alwaysOpen: true,
      note: 'Access via steep staircase; not recommended for those with mobility issues'
    },
    features: {
      length: '300 m',
      lifeguard: 'Seasonal',
      restrooms: 'Limited',
      sunbeds: 'For rent in summer',
      snackBar: 'Seasonal',
      waterSports: 'Surfing',
      parking: 'Available at the top of the cliffs',
      waterQuality: 'Excellent',
      sandType: 'Golden sand',
      waves: 'Moderate to strong',
      wind: 'Exposed to north and west winds',
      facilities: ['Beach bar (seasonal)', 'Toilets (summer only)', 'Showers']
    },
    tips: [
      'Great spot for experienced surfers',
      'Visit at low tide for more beach space',
      'Bring your own supplies as facilities are limited',
      'Wear appropriate footwear for the descent',
      'Stay aware of tide times when exploring the caves'
    ],
    bestTimeToVisit: ['morning', 'afternoon'],
    averageVisitDuration: 150,
    rating: 4.7,
    reviewCount: 9800,
    address: 'Praia do Beliche, 8650-357 Sagres, Portugal',
    transportation: {
      car: 'Parking available at the top of the cliffs',
      bus: 'From Sagres town center to Beliche',
      taxi: 'Available throughout Sagres',
      walking: '45-minute walk from Sagres town center (challenging)'
    },
    surfing: {
      bestSeason: 'Autumn and winter',
      swellDirection: 'NW, W, SW',
      windDirection: 'NE, E, SE',
      waveType: 'Beach break',
      difficulty: 'Intermediate to advanced',
      bottom: 'Sand',
      crowd: 'Moderate',
      hazards: 'Strong currents, rocks'
    },
    nearbyAttractions: [
      'Cabo de São Vicente (5 min drive)',
      'Fortaleza de Sagres (10 min drive)',
      'Praia do Tonel (15 min drive)'
    ]
  }),

  'sagres-harbor': createPoi({
    id: 'sagres-harbor',
    title: 'Sagres Harbor & Downtown',
    description: 'The charming heart of Sagres, where the bustling harbor meets the vibrant town center. This picturesque fishing port is lined with colorful boats, traditional seafood restaurants, and cozy cafes, creating a perfect blend of local life and tourist appeal. The harbor area serves as the social hub of Sagres, where fishermen bring in their daily catch and visitors gather to enjoy fresh seafood while watching the sunset over the Atlantic. The adjacent downtown area features whitewashed buildings, surf shops, and a laid-back bohemian atmosphere that captures the essence of coastal Algarve life.',
    city: 'Sagres',
    categories: ['harbor', 'dining', 'shopping', 'sunset', 'local culture'],
    lat: 37.0,
    lng: -8.9333,
    images: [
      'https://images.pexels.com/photos/5546724/pexels-photo-5546724.jpeg?auto=compress&cs=tinysrgb&w=600',
      'https://images.unsplash.com/photo-1580070142211-03f8c9f3d0c9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      'https://images.unsplash.com/photo-1583619430797-8c8d84541ad1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'
    ],
    contact: {
      website: 'https://www.visitalgarve.pt/en/explorar/sagres/porto-da-baleeira/',
      phone: '+351 282 624 873',
      email: 'turismo@cm-viladobispo.pt'
    },
    hours: {
      alwaysOpen: true,
      fishMarket: '06:00-13:00 (Monday-Saturday)',
      restaurants: {
        breakfast: '08:00-11:30',
        lunch: '12:00-15:00',
        dinner: '18:30-22:30',
        bars: '10:00-02:00 (some later)',
        note: 'Hours may vary by establishment and season'
      },
      shops: '09:30-13:00, 14:30-19:00 (Monday-Saturday)',
      bestTime: 'Morning for market and activities, evening for dining and sunset'
    },
    price: {
      food: '€€-€€€',
      drinks: '€1.50-€5 for beer/coffee, €3-€10 for cocktails',
      boatTours: '€25-€50 per person',
      parking: '€1-€2 per hour',
      currency: 'EUR',
      note: 'Prices may be higher during peak season (June-September)'
    },
    features: {
      dining: 'Wide variety of restaurants and cafes',
      shopping: 'Surf shops, boutiques, and souvenir stores',
      activities: ['Boat tours', 'Fishing trips', 'Dolphin watching', 'Surf lessons'],
      facilities: ['Public restrooms', 'ATM', 'Tourist information', 'First aid'],
      accessibility: 'Partially wheelchair accessible (some uneven surfaces)',
      wifi: 'Available in most cafes and restaurants',
      parking: 'Paid parking available near the harbor'
    },
    tips: [
      'Arrive early (before 8:30 AM) to see the fishing boats unload their catch',
      'Try the local specialty "percebes" (gooseneck barnacles) at a harbor restaurant',
      'Visit the Mercado Municipal for fresh produce and local products',
      'Book boat tours in advance during peak season',
      'Walk along the harbor wall for the best sunset views',
      'Look for the "Rota dos Pescadores" (Fisherman\'s Trail) signs for scenic coastal walks',
      'Visit the small chapel Nossa Senhora da Graça near the harbor'
    ],
    bestTimeToVisit: ['morning', 'late afternoon', 'evening'],
    averageVisitDuration: 120,
    rating: 4.6,
    reviewCount: 12800,
    lastUpdated: '2024-06-06',
    address: 'Porto da Baleeira, 8650-357 Sagres, Portugal',
    transportation: {
      walking: 'Central location, easy to explore on foot',
      parking: 'Paid parking available (€1-2/hour) near the harbor',
      bus: 'Regular buses from Lagos and other Algarve towns stop at Sagres bus station (5 min walk)',
      taxi: 'Available throughout Sagres (€5-10 within town)',
      bike: 'Bike rentals available from several shops in town'
    },
    shopping: {
      types: ['Surf/skate shops', 'Local crafts', 'Souvenirs', 'Beachwear'],
      specialties: ['Handmade ceramics', 'Cork products', 'Local honey and jams', 'Surf gear']
    },
    dining: {
      cuisine: ['Seafood', 'Portuguese', 'Mediterranean', 'International', 'Vegetarian options'],
      priceRange: '€€-€€€',
      specialties: [
        'Cataplana de marisco (seafood stew)',
        'Sardinhas assadas (grilled sardines)',
        'Arroz de marisco (seafood rice)',
        'Percebes (gooseneck barnacles)',
        'Pastel de nata (custard tart)'
      ],
      diningAreas: ['Harborside terraces', 'Rooftop bars', 'Cozy indoor restaurants', 'Beachfront cafes']
    },
    nightlife: {
      options: ['Chill-out bars', 'Live music venues', 'Beach clubs', 'Wine bars'],
      atmosphere: 'Relaxed and casual, with a surf/bohemian vibe',
      bestFor: ['Sunset drinks', 'After-dinner cocktails', 'Live music', 'Meeting fellow travelers']
    },
    nearbyAttractions: [
      'Fortaleza de Sagres (5 min drive / 20 min walk)',
      'Praia da Mareta (5 min walk)',
      'Cabo de São Vicente (15 min drive)',
      'Praia do Martinhal (5 min drive)',
      'Sagres Fortress (5 min drive)'
    ],
    events: {
      weekly: ['Fish market (daily except Sunday)', 'Local craft market (seasonal)'],
      annual: [
        'Sagres Birdwatching Festival (October)',
        'Sagres International Kitesurfing Festival (July)',
        'Festival da Batata Doce (Sweet Potato Festival, November)'
      ]
    }
  }),

  'praia-do-tonel': createPoi({
    id: 'praia-do-tonel',
    title: 'Praia do Tonel',
    description: 'Popular surf spot with strong waves, dramatic cliffs, and a wide stretch of golden sand, offering stunning views of Sagres Fortress.',
    city: 'Sagres',
    categories: ['beach', 'surfing', 'nature'],
    lat: 37.0,
    lng: -8.95,
    images: ['https://images.unsplash.com/photo-1501785888041-af3ef285b470?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    contact: {
      website: 'https://www.visitalgarve.pt/en/explorar/praias/praia-do-tonel/'
    },
    hours: {
      alwaysOpen: true,
      note: 'Lifeguard service available in summer months',
      bestSurf: 'Morning and late afternoon'
    },
    features: {
      length: '800 m',
      width: '100 m',
      lifeguard: 'Seasonal',
      showers: true,
      restrooms: 'Summer only',
      sunbeds: 'For rent in summer',
      snackBar: 'Seasonal',
      waterSports: 'Surfing, bodyboarding',
      parking: true,
      waterQuality: 'Excellent',
      sandType: 'Golden sand',
      waves: 'Moderate to strong',
      wind: 'Exposed to north and west winds',
      facilities: ['Beach bar (summer)', 'Toilets', 'Outdoor showers']
    },
    tips: [
      'Ideal for experienced surfers due to strong currents',
      'Visit at low tide for more beach space',
      'Bring your own supplies as facilities are limited',
      'Great spot for sunset photography',
      'Be cautious of strong rip currents'
    ],
    bestTimeToVisit: ['morning', 'afternoon'],
    averageVisitDuration: 120,
    rating: 4.6,
    reviewCount: 10500,
    address: 'Praia do Tonel, 8650-357 Sagres, Portugal',
    transportation: {
      car: 'Parking available near the beach',
      taxi: 'Available from Sagres town',
      walking: '20-minute walk from Sagres town center',
      bike: 'Bike path available part of the way'
    },
    surfing: {
      bestSeason: 'Autumn to spring',
      swellDirection: 'W, NW, SW',
      windDirection: 'NE, E, SE',
      waveType: 'Beach break',
      difficulty: 'Intermediate to advanced',
      bottom: 'Sand',
      crowd: 'Moderate to crowded in summer',
      hazards: 'Strong currents, rocks',
      surfSchools: ['Sagres Surf School', 'Tonel Surf School']
    },
    nearbyAttractions: [
      'Fortaleza de Sagres (5 min drive)',
      'Praia da Mareta (5 min drive)',
      'Cabo de São Vicente (10 min drive)'
    ]
  }),

  'cervejaria-ramiro': createPoi({
    id: 'cervejaria-ramiro',
    title: 'Cervejaria Ramiro',
    description: 'Legendary seafood restaurant famous for its garlic shrimp and seafood platters, a must-visit for food lovers in Lisbon.',
    city: 'Lisbon',
    categories: ['restaurant', 'seafood', 'local-cuisine'],
    lat: 38.7170,
    lng: -9.1481,
    imageUrl: 'https://images.unsplash.com/photo-1475905760530-a22eda0fb1b3?q=80&w=2075&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    images: [
      'https://images.unsplash.com/photo-1475905760530-a22eda0fb1b3?q=80&w=2075&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'
    ],
    contact: {
      phone: '+351 21 885 1024',
      website: 'https://www.cervejariaramiro.pt',
      email: 'info@cervejariaramiro.pt'
    },
    hours: {
      tuesday: '12:00-00:30',
      wednesday: '12:00-00:30',
      thursday: '12:00-00:30',
      friday: '12:00-00:30',
      saturday: '12:00-00:30',
      sunday: '12:00-00:30',
      monday: 'Closed',
      note: 'No reservations, expect to wait during peak hours'
    },
    price: {
      range: '€€€',
      currency: 'EUR',
      note: 'Expect to spend around €30-50 per person'
    },
    features: {
      wheelchairAccess: true,
      restrooms: true
    },
    tips: [
      'No reservations - expect to wait during peak hours',
      'Try the prego (steak sandwich) for dessert',
      'Ask for the daily specials',
      'Cash only - no credit cards accepted'
    ],
    bestTimeToVisit: ['lunch', 'dinner'],
    averageVisitDuration: 90,
    rating: 4.7,
    reviewCount: 12500,
    lastUpdated: '2024-05-20',
    address: 'Avenida Almirante Reis 1, 1150-038 Lisboa, Portugal'
  }),

  'sao-jorge-castle': createPoi({
    id: 'sao-jorge-castle',
    title: 'Castelo de São Jorge',
    description: 'Iconic hilltop castle with panoramic views of Lisbon, historic battlements, and peacocks roaming the grounds.',
    city: 'Lisbon',
    categories: ['landmark', 'historic', 'viewpoint'],
    lat: 38.7139,
    lng: -9.1335,
    imageUrl: 'https://images.unsplash.com/photo-1721932413608-6ba8e294e04d?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    images: [
      'https://images.unsplash.com/photo-1721932413608-6ba8e294e04d?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      'https://images.unsplash.com/photo-1585533029665-6fb5cdfd07b3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      'https://images.unsplash.com/photo-1584291527905-7c0ca0db921d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'
    ],
    contact: {
      phone: '+351 21 880 0620',
      website: 'https://castelodesaojorge.pt/en/',
      email: 'info@castelodesaojorge.pt'
    },
    hours: {
      january: '09:00-18:00',
      february: '09:00-18:00',
      march: '09:00-19:00',
      april: '09:00-21:00',
      may: '09:00-21:00',
      june: '09:00-21:00',
      july: '09:00-21:00',
      august: '09:00-21:00',
      september: '09:00-21:00',
      october: '09:00-19:00',
      november: '09:00-18:00',
      december: '09:00-18:00',
      lastEntry: '30 minutes before closing',
      closed: 'January 1, May 1, December 24, 25, 31'
    },
    price: {
      adult: 10,
      senior: 8.5,
      student: 5,
      youth: 5,
      child: 0,
      family: 25,
      currency: 'EUR',
      note: 'Free admission with Lisboa Card'
    },
    features: {
      wheelchairAccess: true,
      guidedTours: true,
      giftShop: true,
      cafe: true,
      restrooms: true
    },
    tips: [
      'Visit early in the morning or late afternoon to avoid crowds',
      'Wear comfortable shoes as there are many stairs',
      'Don\'t miss the Camera Obscura for amazing 360° views',
      'The sunset views over the city are spectacular',
      'Free admission on the first Sunday of each month'
    ],
    bestTimeToVisit: ['morning', 'sunset'],
    averageVisitDuration: 120,
    rating: 4.5,
    reviewCount: 45000,
    lastUpdated: '2024-05-20',
    address: 'R. de Santa Cruz do Castelo, 1100-129 Lisboa, Portugal'
  }),

  'praia-da-oura': createPoi({
    id: 'praia-da-oura',
    title: 'Praia da Oura',
    description: 'One of the most famous beaches in the Algarve, Praia da Oura is known for its golden sand, clear waters, and vibrant atmosphere. The beach stretches along a beautiful coastline with dramatic cliffs and rock formations, offering excellent swimming conditions and water sports opportunities. The surrounding area is lively with beach bars, restaurants, and entertainment options, making it a favorite among both families and young travelers.',
    city: 'Albufeira',
    categories: ['beach', 'nature', 'swimming', 'sunset'],
    lat: 37.0920,
    lng: -8.2400,
    images: [
      'https://images.pexels.com/photos/16021281/pexels-photo-16021281/free-photo-of-photo-of-a-seaside-town-in-portugal.jpeg?auto=compress&cs=tinysrgb&w=600',
      'https://images.unsplash.com/photo-1599481238640-4c1288750d7a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1412&q=80'
    ],
    contact: {
      website: 'https://www.visitalgarve.pt/en/1170/praia-da-oura.aspx'
    },
    hours: {
      alwaysOpen: true,
      note: 'Lifeguards on duty during summer months (June-September)'
    },
    price: {
      entry: 'Free',
      sunbed: '€10-15 per day',
      umbrella: '€10-15 per day',
      parking: '€2-5 per hour',
      currency: 'EUR'
    },
    features: {
      facilities: ['Showers', 'Toilets', 'Sunbeds for rent', 'Beach bars', 'Water sports equipment'],
      activities: ['Swimming', 'Beach volleyball', 'Jet skiing', 'Parasailing', 'Boat tours'],
      accessibility: 'Wooden walkway to the beach, but some areas may be challenging for wheelchairs',
      lifeguard: 'Seasonal (June-September)',
      parking: 'Paid parking available nearby',
      restaurants: 'Multiple beachfront restaurants and bars',
      waterQuality: 'Excellent, Blue Flag certified'
    },
    tips: [
      'Visit early in the morning to secure a good spot during peak season',
      'Try the fresh seafood at one of the beachfront restaurants',
      'Stay for sunset - the views are spectacular',
      'Bring water shoes as the sand can get very hot',
      'Consider a boat tour to explore nearby caves and grottos'
    ],
    bestTimeToVisit: ['morning', 'late afternoon'],
    averageVisitDuration: 180,
    rating: 4.6,
    reviewCount: 8500,
    lastUpdated: '2024-06-06',
    address: 'Praia da Oura, 8200-916 Albufeira, Portugal'
  }),

  'benagil-caves': createPoi({
    id: 'benagil-caves',
    title: 'Benagil Caves',
    description: 'The Benagil Caves are one of the most stunning natural attractions in the Algarve, featuring a series of dramatic sea caves carved into the golden limestone cliffs. The highlight is the famous Benagil Cave (Algar de Benagil), with its iconic circular hole in the ceiling that allows sunlight to illuminate the crystal-clear waters below. Accessible only by boat, kayak, or stand-up paddleboard, these caves offer a magical experience with their turquoise waters, hidden beaches, and impressive rock formations. The area is part of the larger Costa d\'Oiro (Golden Coast) and is best explored during the calmer morning waters.',
    city: 'Lagos',
    categories: ['beach', 'cave', 'boat-tour', 'nature', 'photography'],
    lat: 37.0883,
    lng: -8.4249,
    contact: {
      phone: '+351 282 000 000',
      email: 'info@benagilcaves.pt',
      website: 'https://www.benagilcaves.com'
    },
    hours: {
      summer: '09:00-19:00 (April to October)',
      winter: '10:00-17:00 (November to March)',
      note: 'Best visited in the morning when the sea is calmer and lighting is ideal for photography. Access depends on sea conditions.'
    },
    price: {
      boatTour: '€25-40 per person',
      kayakRental: '€30-50 per person',
      paddleboardRental: '€25-45 per person',
      privateBoat: '€100-200 (up to 8 people)',
      currency: 'EUR',
      note: 'Prices vary by operator, tour duration, and inclusions (some include stops for swimming and snorkeling)'
    },
    features: {
      wheelchairAccess: false,
      restrooms: true,
      parking: true,
      lifeJackets: true,
      changingRooms: true,
      showerFacilities: true,
      giftShop: true,
      snackBar: true,
      guidedTours: true,
      equipmentRental: true
    },
    images: [
      'https://images.pexels.com/photos/12228100/pexels-photo-12228100.jpeg?auto=compress&cs=tinysrgb&w=600',
      'https://images.unsplash.com/photo-1501785888041-af3ef285b470?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      'https://images.unsplash.com/photo-1501785888041-af3ef285b470?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'
    ],
    rating: 4.8,
    reviewCount: 12500,
    averageVisitDuration: 180,
    bestTimeToVisit: ['April to October', 'Morning hours', 'Low tide'],
    topAttractions: [
      'Algar de Benagil (Main Cave)',
      'Benagil Beach',
      'Marinha Beach',
      'Carvalho Beach',
      'Seven Hanging Valleys Trail'
    ],
    tips: [
      'Book boat tours in advance during peak season',
      'Wear water shoes for comfort on the boats and beaches',
      'Bring a waterproof camera or phone case',
      'Apply reef-safe sunscreen before the tour',
      'Consider a kayak or SUP tour for a more intimate experience',
      'Check the weather and sea conditions before going',
      'Arrive early to avoid crowds and secure parking'
    ],
    dining: {
      nearby: 'Several restaurants in Benagil village',
      priceRange: '€€-€€€',
      specialties: [
        'Fresh seafood dishes',
        'Grilled sardines',
        'Cataplana (seafood stew)',
        'Local wines'
      ]
    },
    transportation: {
      car: '20 min from Lagos, 45 min from Faro',
      parking: 'Paid parking available near Benagil Beach',
      bus: 'Limited service from Lagos to Benagil',
      taxi: 'Available from Lagos and surrounding areas',
      tour: 'Many operators offer hotel pickup from major Algarve resorts'
    },
    events: {
      annual: [
        'Sunrise Yoga in the Caves (Summer months)',
        'Full Moon Kayak Tours (Summer)',
        'Photography Workshops (Spring and Fall)'
      ]
    },
    historicalSignificance: 'The Benagil Caves were formed over millions of years through the erosive power of the Atlantic Ocean on the limestone cliffs of the Algarve coast. While the exact timeline of their discovery is unknown, they have become one of Portugal\'s most iconic natural landmarks in recent decades, gaining international fame through social media. The caves are part of the larger Costa d\'Oiro (Golden Coast) and are protected as part of the Natura 2000 network of nature protection areas in Europe.'
  }),

  'douro-river-sunset-cruise': createPoi({
    id: 'douro-river-sunset-cruise',
    title: 'Douro River Sunset Cruise',
    description: 'Experience the magical beauty of the Douro Valley from the water on a breathtaking sunset cruise. As the sun dips below the terraced vineyards, the landscape transforms into a golden paradise, creating unforgettable memories. Sail past traditional wine estates, charming riverside villages, and the UNESCO-listed vineyards that make this region famous. The cruise offers a unique perspective of the valley\'s dramatic landscapes and is the perfect way to end a day of wine tasting in the Douro.',
    city: 'Douro',
    categories: ['boat-tour', 'sunset', 'scenic', 'romantic', 'river-cruise'],
    lat: 41.1000,
    lng: -7.8000,
    contact: {
      phone: '+351 22 000 0000',
      email: 'info@dourorivercruises.pt',
      website: 'https://www.dourorivercruises.pt'
    },
    hours: {
      aprilToOctober: 'Departures at 18:30 (varies by season)',
      novemberToMarch: 'Departures at 17:00 (varies by season)',
      note: 'Boarding begins 30 minutes before departure. Times vary throughout the year based on sunset.'
    },
    price: {
      adult: '€35-75',
      child: '€15-35 (ages 4-12)',
      privateBoat: '€200-400 (up to 8 people)',
      currency: 'EUR',
      note: 'Prices vary by cruise duration, inclusions, and boat type. Discounts available for online bookings.'
    },
    features: {
      wheelchairAccess: true,
      restrooms: true,
      parking: true,
      bar: true,
      snacks: true,
      audioGuide: true,
      englishSpeakingCrew: true,
      upperDeck: true,
      coveredAreas: true,
      lifeJackets: true
    },
    images: [
      'https://images.pexels.com/photos/9756283/pexels-photo-9756283.jpeg?auto=compress&cs=tinysrgb&w=1200',
      'https://images.unsplash.com/photo-1580070142211-03f8c9f3d0c9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      'https://images.unsplash.com/photo-1623078478883-9d1f9d4b8b0a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80'
    ],
    rating: 4.9,
    reviewCount: 3420,
    averageVisitDuration: 120,
    bestTimeToVisit: ['May to September', '1-2 hours before sunset'],
    topAttractions: [
      'Golden Hour on the Douro',
      'Vineyard Terraces',
      'Pinhão Village',
      'Pipadouro Viewpoint',
      'Quinta do Crasto Viewpoint'
    ],
    tips: [
      'Book at least 2-3 days in advance during peak season',
      'Bring a light jacket as it can get breezy on the water',
      'Arrive early for the best seats on the upper deck',
      'The light is best for photography about 30 minutes before sunset',
      'Combine with a wine tasting at a nearby quinta before the cruise',
      'Check the weather forecast and dress accordingly'
    ],
    dining: {
      onboard: 'Wine and snacks available for purchase',
      nearby: 'Several restaurants in Pinhão and Peso da Régua',
      priceRange: '€€-€€€',
      specialties: [
        'Local wines by the glass or bottle',
        'Portuguese tapas and snacks',
        'Sunset cocktail specials'
      ]
    },
    transportation: {
      meetingPoint: 'Pinhão or Peso da Régua docks',
      parking: 'Paid parking available near departure points',
      train: 'Pinhão train station (5 min walk to dock)',
      taxi: 'Available from surrounding areas',
      note: 'Some operators offer hotel pickup from Porto for an additional fee'
    },
    events: {
      seasonal: [
        'Harvest Moon Cruises (September-October)',
        'Starlit Dinner Cruises (Summer months)',
        'New Year\'s Eve Fireworks Cruise'
      ]
    },
    historicalSignificance: 'The Douro River has been the lifeblood of the region for centuries, serving as the main transportation route for the famous Port wine barrels on traditional Rabelo boats. These flat-bottomed boats were historically used to transport wine from the vineyards to the cellars in Porto. Today, they offer visitors a glimpse into the region\'s winemaking heritage while providing one of the most scenic river experiences in Europe.'
  }),

  'lagos-old-town': createPoi({
    id: 'lagos-old-town',
    title: 'Lagos Old Town',
    description: 'Step back in time as you wander through the charming cobbled streets of Lagos Old Town, a picturesque historic center that perfectly blends traditional Portuguese architecture with a vibrant modern atmosphere. This pedestrian-friendly area is filled with whitewashed buildings adorned with colorful borders, traditional Portuguese pavement, and historic landmarks around every corner. The old town is home to numerous restaurants, cafés, and boutique shops, making it the perfect place to experience authentic Portuguese culture and cuisine. Don\'t miss the beautiful churches, the old slave market (Mercado de Escravos), and the stunning views from the city walls.',
    city: 'Lagos',
    categories: ['historic', 'shopping', 'dining', 'walking-tour', 'culture'],
    lat: 37.1028,
    lng: -8.6741,
    contact: {
      phone: '+351 282 000 000',
      email: 'turismo@cm-lagos.pt',
      website: 'https://www.visitalgarve.pt/en/explorar/areas/lagos/'
    },
    hours: {
      alwaysOpen: 'Outdoor areas accessible 24/7',
      shops: '10:00-19:00 (varies by store)',
      restaurants: '12:00-23:00 (varies by establishment)',
      note: 'Best visited in the morning or late afternoon to avoid the midday heat.'
    },
    price: {
      free: 'Free to explore',
      guidedTour: '€10-20 per person',
      currency: 'EUR',
      note: 'Some attractions within the old town may have separate entrance fees.'
    },
    features: {
      wheelchairAccess: 'Partial (cobblestone streets may be challenging)',
      restrooms: 'Available in cafes and restaurants',
      parking: 'Paid parking nearby',
      giftShops: true,
      cafes: true,
      restaurants: true,
      atms: true,
      wifi: 'Available in most cafes and restaurants'
    },
    images: [
      'https://images.pexels.com/photos/12996527/pexels-photo-12996527.jpeg?auto=compress&cs=tinysrgb&w=600',
      'https://images.unsplash.com/photo-1580757468214-98ab5b0f0d6c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      'https://images.unsplash.com/photo-1566438480904-70b2d8e7c7e5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'
    ],
    rating: 4.7,
    reviewCount: 12500,
    averageVisitDuration: 180,
    bestTimeToVisit: ['Spring', 'Autumn', 'Early morning or late afternoon'],
    topAttractions: [
      'Igreja de Santo António',
      'Castelo dos Governadores',
      'Mercado Municipal de Lagos',
      'Praça Gil Eanes',
      'Rua 25 de Abril'
    ],
    tips: [
      'Wear comfortable walking shoes for the cobblestone streets',
      'Visit the local market for fresh produce and souvenirs',
      'Try the local pastry \'Dom Rodrigo\' in traditional bakeries',
      'Take a guided walking tour to learn about the rich history',
      'Visit in the evening for a lively atmosphere and great dining options'
    ],
    dining: {
      cuisine: ['Seafood', 'Traditional Portuguese', 'International'],
      priceRange: '€-€€€',
      specialties: [
        'Cataplana de marisco (seafood stew)',
        'Grilled sardines',
        'Piri-piri chicken',
        'Local wines and medronho (local brandy)'
      ]
    },
    transportation: {
      car: 'Paid parking available at Avenida dos Descobrimentos',
      bus: 'Local bus stops throughout the city',
      train: 'Lagos train station (15 min walk)',
      walking: 'Easily walkable from most central Lagos locations'
    },
    events: {
      annual: [
        'Medieval Fair (August)',
        'Lagos Jazz Festival (July)',
        'Christmas Market (December)'
      ]
    },
    historicalSignificance: 'Lagos has a rich history dating back to Roman times, but it was during the Age of Discovery in the 15th century that the city flourished as a major port. Many famous explorers, including Henry the Navigator, used Lagos as a base for their voyages. The old town still retains much of its historic character, with buildings and churches that reflect its golden age as a center of maritime exploration.'
  }),

  'giraldo-square': createPoi({
    id: 'giraldo-square',
    title: 'Giraldo Square (Praça do Giraldo)',
    description: 'Praça do Giraldo is the vibrant heart of Évora, a UNESCO World Heritage city in Portugal\'s Alentejo region. This elegant square is surrounded by historic buildings featuring a mix of architectural styles, including Gothic, Manueline, and Moorish influences. The square is named after Geraldo Geraldes, the fearless knight who reconquered Évora from the Moors in 1167. Today, it serves as a lively gathering place with outdoor cafes, shops, and street performers, making it the perfect spot to experience the local atmosphere and enjoy traditional Alentejo cuisine.',
    city: 'Évora',
    categories: ['square', 'historic', 'city-center', 'architecture', 'dining'],
    lat: 38.5710,
    lng: -7.9076,
    contact: {
      phone: '+351 266 777 071',
      email: 'posto.central@cm-evora.pt',
      website: 'https://www.cm-evora.pt/'
    },
    hours: {
      square: 'Always open',
      shops: '10:00-19:00 (Mon-Sat, some close for lunch)',
      restaurants: '12:00-23:00',
      touristOffice: '09:00-18:00 (Mon-Fri), 09:00-13:00 & 14:00-18:00 (Sat-Sun)'
    },
    price: {
      squareAccess: 'Free',
      attractions: 'Varies by site',
      guidedTour: '€10-20 per person',
      currency: 'EUR',
      note: 'Many surrounding attractions have separate entrance fees.'
    },
    features: {
      wheelchairAccess: 'Yes (cobbled surface)',
      restrooms: 'Public facilities available',
      parking: 'Paid parking nearby',
      cafes: true,
      restaurants: true,
      shops: true,
      atm: true,
      wifi: 'Available in some cafes',
      touristInfo: 'Available at the tourist office on the square'
    },
    images: [
      'https://media.istockphoto.com/id/877850560/photo/the-giraldo-square.jpg?b=1&s=612x612&w=0&k=20&c=8pK2DFF81jboHnzrGsF7GV1N8APb7KNTtA485vRC3lk=',
      'https://images.unsplash.com/photo-1580489944761-6baf8b7c1ce3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1471&q=80',
      'https://images.unsplash.com/photo-1580489944761-6baf8b7c1ce3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1471&q=80'
    ],
    rating: 4.8,
    reviewCount: 12500,
    averageVisitDuration: 90,
    bestTimeToVisit: ['Spring', 'Autumn', 'Mornings and evenings', 'Weekdays (less crowded)'],
    topAttractions: [
      'Santo Antão Church',
      'Fountain of the Eight Spouts',
      'Évora Cathedral',
      'Roman Temple',
      'Chapel of Bones'
    ],
    tips: [
      'Try the local pastries at Pastelaria Conventual Pão de Rala',
      'Visit the tourist office for maps and information',
      'Look for the marble pillory in the square',
      'Enjoy a coffee at one of the outdoor cafes',
      'Visit the nearby Évora Museum for local history'
    ],
    dining: {
      type: ['Alentejo cuisine', 'Traditional Portuguese', 'Cafes'],
      priceRange: '€€-€€€',
      specialties: [
        'Migas (bread-based dish with meat)',
        'Açorda à Alentejana (bread soup)',
        'Sericaia (egg pudding)',
        'Alentejo wines'
      ]
    },
    transportation: {
      parking: 'Paid parking available nearby',
      bus: 'Local and regional buses stop nearby',
      taxi: 'Available at the square',
      walking: 'Central location, walkable from most city center hotels'
    },
    events: {
      weekly: [
        'Farmers Market (Tuesday and Saturday mornings)',
        'Antique Fair (first Tuesday of each month)'
      ],
      annual: [
        'Feira de São João (June)',
        'Évora Classical Music Festival (July)',
        'Christmas Market (December)'
      ]
    },
    historicalSignificance: 'Praça do Giraldo has been the heart of Évora since the Middle Ages. The square was originally the site of the Roman forum and later became the main marketplace. It was completely rebuilt in the 16th century and named after Geraldo Geraldes, the Christian knight who conquered Évora from the Moors. The square has witnessed many important historical events, including public executions during the Inquisition. Today, it remains a vibrant center of city life, surrounded by historic buildings that showcase Évora\'s rich architectural heritage.'
  }),

  'chapel-of-bones': createPoi({
    id: 'chapel-of-bones',
    title: 'Chapel of Bones (Capela dos Ossos)',
    description: 'The Chapel of Bones is one of Évora\'s most famous and macabre attractions. This small interior chapel is part of the larger Church of St. Francis and is entirely lined with the bones and skulls of an estimated 5,000 people, exhumed from the city\'s medieval cemeteries. Built in the 16th century by a Franciscan monk who wanted to convey the message of life\'s transience, the chapel features an inscription above the entrance that reads: \'We bones that are here, for yours we wait.\' The chapel serves as a powerful memento mori, reminding visitors of the inevitability of death and the fleeting nature of human life.',
    city: 'Évora',
    categories: ['historic', 'religious', 'museum', 'unique', 'architecture'],
    lat: 38.5676,
    lng: -7.9076,
    contact: {
      phone: '+351 266 704 521',
      email: 'geral@ordemsaofrancisco.pt',
      website: 'https://www.capeladosossos.pt/'
    },
    hours: {
      novemberToMarch: '09:30-17:00 (last entry 16:30)',
      aprilToOctober: '09:30-18:30 (last entry 18:00)',
      closed: 'January 1, Easter Sunday, May 1, December 25',
      note: 'Last admission 30 minutes before closing.'
    },
    price: {
      adult: '€6',
      senior: '€5 (65+)',
      student: '€4 (with valid ID)',
      child: 'Free (under 12)',
      family: '€14 (2 adults + 2 children)',
      currency: 'EUR',
      note: 'Combined ticket available with the Church of St. Francis.'
    },
    features: {
      wheelchairAccess: 'Partial (some areas may be difficult to access)',
      restrooms: true,
      giftShop: true,
      photography: 'Allowed (no flash)',
      guidedTours: true,
      audioGuide: true,
      timeSlot: 'Recommended 20-30 minutes for visit'
    },
    images: [
      'https://images.pexels.com/photos/12746196/pexels-photo-12746196.jpeg?auto=compress&cs=tinysrgb&w=600',
      'https://images.unsplash.com/photo-1580757468214-98ab5b0f0d6c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      'https://images.unsplash.com/photo-1566438480904-70b2d8e7c7e5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'
    ],
    rating: 4.5,
    reviewCount: 18700,
    averageVisitDuration: 30,
    bestTimeToVisit: ['Weekday mornings', 'Low season (Nov-Mar)', 'Last hour before closing'],
    topAttractions: [
      'Bone-covered walls and columns',
      'Skull ceiling vault',
      '16th-century mummies',
      'Religious art collection',
      'Terraces with city views'
    ],
    tips: [
      'Visit early in the day to avoid crowds',
      'Consider a guided tour for historical context',
      'Be respectful as it\'s a religious site',
      'Not recommended for very young children or sensitive visitors',
      'Combine with a visit to the Church of St. Francis'
    ],
    nearbyAttractions: [
      'Church of St. Francis (Igreja de São Francisco)',
      'Giraldo Square',
      'Évora Cathedral',
      'Roman Temple',
      'University of Évora'
    ],
    transportation: {
      walking: '10-15 minute walk from Giraldo Square',
      bus: 'City bus stops nearby',
      parking: 'Paid parking available at Praça de Giraldo',
      accessibility: 'Limited wheelchair access due to historic nature of building'
    },
    events: {
      annual: [
        'Night visits during Évora Classical Music Festival (July)',
        'Special All Saints\' Day program (November 1st)'
      ]
    },
    historicalSignificance: 'The Chapel of Bones was built in the late 16th century by Franciscan monks who wanted to convey the message of life\'s transience, a common theme during the Counter-Reformation period. The bones were exhumed from local churchyards and cemeteries when Évora\'s population grew and the city needed more space. The chapel\'s design was inspired by other European ossuaries, particularly the Capela dos Ossos in Évora\'s sister city of Faro. The chapel is part of the larger Church of St. Francis complex and remains one of Portugal\'s most visited historical sites, offering a unique perspective on mortality and the passage of time.'
  }),

  'temple-of-diana': createPoi({
    id: 'temple-of-diana',
    title: 'Temple of Diana (Templo Romano de Évora)',
    description: 'The Temple of Diana is one of the most iconic landmarks of Évora and one of the best-preserved Roman ruins in Portugal. This well-preserved Roman temple dates back to the 1st century AD and stands as a testament to the city\'s Roman past. Despite its common name, the temple was actually dedicated to the Roman Emperor Augustus, who was worshipped as a god. The temple features 14 original granite Corinthian columns, with their marble capitals still intact, standing on a raised platform in the historic center. The site offers a fascinating glimpse into the Roman occupation of the Iberian Peninsula and is beautifully illuminated at night, creating a magical atmosphere.',
    city: 'Évora',
    categories: ['archaeological', 'roman', 'historic', 'landmark', 'unesco'],
    lat: 38.5728,
    lng: -7.9076,
    contact: {
      phone: '+351 266 777 072',
      email: 'posto.central@cm-evora.pt',
      website: 'https://www.cm-evora.pt/'
    },
    hours: {
      exterior: '24/7 (visible from outside)',
      museum: '09:30-17:30 (Tue-Sun)',
      closed: 'Mondays, January 1, Easter Sunday, May 1, December 25',
      bestView: 'Early morning or sunset for photography'
    },
    price: {
      exterior: 'Free to view from outside',
      museum: 'Included in Évora City Card',
      guidedTour: 'Starting from €15 per person',
      currency: 'EUR',
      note: 'Viewing the temple from the outside is free; museum admission may apply.'
    },
    features: {
      wheelchairAccess: 'Partial (exterior only)',
      restrooms: 'Nearby in the museum',
      giftShop: 'Available in the museum',
      guidedTours: true,
      audioGuide: true,
      lighting: 'Beautifully illuminated at night'
    },
    images: [
      'https://images.pexels.com/photos/17279595/pexels-photo-17279595/free-photo-of-roman-temple-of-evora-and-the-church-of-the-cadaval-palace-in-portugal.jpeg?auto=compress&cs=tinysrgb&w=600',
      'https://images.unsplash.com/photo-1580757468214-98ab5b0f0d6c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      'https://images.unsplash.com/photo-1566438480904-70b2d8e7c7e5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'
    ],
    rating: 4.8,
    reviewCount: 12500,
    averageVisitDuration: 30,
    bestTimeToVisit: ['Early morning', 'Late afternoon', 'Evening'],
    topAttractions: [
      'Corinthian columns',
      'Original Roman platform',
      'Museum exhibits',
      'Night illumination',
      'Panoramic views of Évora'
    ],
    tips: [
      'Visit at sunrise or sunset for the best lighting and fewer crowds',
      'The temple is beautifully illuminated at night',
      'Wear comfortable shoes as the area is cobbled',
      'Combine with a visit to the nearby Cathedral and Giraldo Square',
      'Look for the medieval tower built into the temple'
    ],
    nearbyAttractions: [
      'Évora Cathedral',
      'Giraldo Square',
      'Chapel of Bones',
      'University of Évora',
      'Roman Baths'
    ],
    transportation: {
      walking: '5-minute walk from Giraldo Square',
      bus: 'City bus stops nearby',
      parking: 'Paid parking available at Praça de Giraldo',
      accessibility: 'Exterior view is wheelchair accessible'
    },
    events: {
      annual: [
        'Roman Festival (June)',
        'European Heritage Days (September)',
        'Night of Museums (May)'
      ]
    },
    historicalSignificance: 'The Temple of Diana is one of the most significant Roman monuments in Portugal and a symbol of Évora\'s rich history. Built in the 1st century AD during the reign of Emperor Augustus, it was part of the Roman forum of the ancient city of Ebora Liberalitas Julia. The temple was preserved when it was incorporated into a medieval tower, which protected it from complete destruction. In the 19th century, it was restored to its current state, and today it stands as a proud reminder of Portugal\'s Roman heritage. The temple is part of the UNESCO World Heritage-listed historic center of Évora.'
  })
};

/**
 * Get detailed information about a POI by its ID
 * @param {string} id - The unique identifier of the POI
 * @returns {Object|null} The POI object or null if not found
 */
export const getPoiDetails = (id) => {
  return poiDatabase[id] || null;
};

/**
 * Get all POIs for a specific city
 * @param {string} city - The name of the city
 * @returns {Array} Array of POI objects in the specified city
 */
export const getPoisByCity = (city) => {
  if (!city) return [];
  const cityLower = city.toLowerCase();
  return Object.values(poiDatabase).filter(poi => 
    poi.city && poi.city.toLowerCase() === cityLower
  );
};

/**
 * Search for POIs by query string
 * @param {string} query - The search term
 * @returns {Array} Array of POI objects matching the search
 */
export const searchPois = (query) => {
  if (!query.trim()) return [];
  
  const searchTerm = query.toLowerCase();
  return Object.values(poiDatabase).filter(poi => 
    (poi.title && poi.title.toLowerCase().includes(searchTerm)) ||
    (poi.description && poi.description.toLowerCase().includes(searchTerm)) ||
    (poi.categories && poi.categories.some(cat => cat.toLowerCase().includes(searchTerm))) ||
    (poi.address && poi.address.toLowerCase().includes(searchTerm))
  );
};

/**
 * Get POIs by category
 * @param {string} category - The category to filter by
 * @returns {Array} Array of POI objects in the specified category
 */
export const getPoisByCategory = (category) => {
  if (!category) return [];
  const categoryLower = category.toLowerCase();
  return Object.values(poiDatabase).filter(poi => 
    poi.categories && poi.categories.some(cat => cat.toLowerCase() === categoryLower)
  );
};

/**
 * Get featured POIs (top rated)
 * @param {number} limit - Maximum number of POIs to return
 * @returns {Array} Array of featured POI objects
 */
export const getFeaturedPois = (limit = 5) => {
  return Object.values(poiDatabase)
    .sort((a, b) => (b.rating || 0) - (a.rating || 0))
    .slice(0, limit);
};
